import "./style.css";
import form720FilerExample from "./config/form720-filer.example.json";
import { clearAuth, isAuthed, tryLogin } from "./auth";
import {
  aggregateByQuarter,
  aggregateExciseByQuarterMonths,
  combineQuarterlyTotals,
  effectiveAlaskaHawaiiDeparturePassengers,
  effectiveDomesticSegmentPassengers,
  effectiveInternationalFacilityPassengers,
  legsInCalendarMonth,
  legAirExcise,
} from "./lib/calculator";
import {
  DEFAULT_HONDA_USD_MONTHLY_PROGRAM,
  DEFAULT_HONDA_USD_PER_FLIGHT_HOUR,
  DEFAULT_HONDA_USD_PER_LANDING,
  type CompanyHondaSummary,
  summarizeCompanyHonda,
} from "./lib/companyHonda";
import {
  effectiveFlightHours,
  effectiveTexasDepartures,
  effectiveTexasLandings,
  fuelCostUsd,
  personalFuelOwed2x,
  resolvedFuelGallons,
  type TexasLandingOptions,
} from "./lib/fuelDerived";
import {
  applyHondaServiceContractToForm,
  HONDA_CONTRACT_DOM_IDS,
  hondaMaintenanceFromForm,
  loadHondaServiceContractFromStorage,
  type HondaMaintenanceSnapshot,
  readHondaServiceContractFromForm,
  saveHondaServiceContractToStorage,
} from "./lib/hondaServiceContract";
import {
  buildFilledForm720Pdf,
  latestQuarterEndingLabel,
  type Form720PdfFiler,
} from "./lib/form720PdfFill";
import { dedupeFlightLegs } from "./lib/flightDedupe";
import {
  filterLegsByPeriod,
  flatFeeMonthsInPeriod,
  lastIsoDayOfCalendarMonth,
  perTripFederalLines,
  pickLastNLegsByDate,
  timeshareRollup,
  toIsoDateLocal,
  totalTakeoffs,
  type PeriodSelection,
  type TimeshareRollup,
  type TripTaxLine,
} from "./lib/periodSummary";
import {
  loadPersonalBasis,
  personalResolverForBasis,
  resolvePersonalLeg,
  type PersonalBasis,
} from "./lib/rollupPersonal";
import {
  loadTexasInferFromDestination,
  texasDestinationIcaoSet,
} from "./lib/texasIcao";
import { loadFlightCsv } from "./lib/csvLoad";
import {
  classifyLegForRollup,
  rollupClassificationSummary,
} from "./lib/legPurposeHeuristic";
import { taxablePassengerListSummary } from "./lib/taxablePassengers";
import { isIPadLike } from "./lib/deviceDetect";
import { money } from "./lib/money";
import {
  personalTransport720BasisLabel,
  personalTransportPercentageTaxByQuarter,
  personalTransportPercentageTaxTotal,
  personalTransportQuarterStats,
  type PersonalTransport720PctBasis,
  type PersonalTransportQuarterStats,
} from "./lib/personalTransport720";
import {
  fillLegRowFromFlightLeg,
  formLegRowHtml,
  hydrateFormWithLegs,
  initLegRowChrome,
  maybeAutoTexasArrival,
  readLegsFromManualForm,
  refreshAllLegRowChrome,
  syncAllFuelGallonsFromLbs,
  updateFuelGalVisibility,
  updateFuelGallonsForRow,
} from "./lib/formLegs";
import { initAirportsAutocomplete } from "./lib/airportsAutocomplete";
import { initPassengersAutocomplete } from "./lib/passengersAutocomplete";
import {
  DEFAULT_AIRCRAFT_ID,
  irs26Total,
  totalExciseAir,
  type FlightLeg,
  type MonthlyForm720Totals,
  type QuarterlyTotals,
  type Rates,
} from "./lib/types";
import {
  bulkImportContacts,
  listContactsAlphabetical,
  searchContacts,
  type ContactImportJson,
} from "./supabase/contacts";
import {
  bundleBuildCommit,
  hasSupabaseConfig,
  supabaseEnvDebug,
} from "./supabase/client";
import {
  insertUgcInvoiceSnapshot,
  listUgcInvoiceSnapshots,
} from "./supabase/ugcInvoiceSnapshots";
import {
  deleteFlightLeg,
  getFlightLeg,
  insertFlightLegs,
  listFlightLegs,
  updateFlightLeg,
  upsertManualLegs,
} from "./supabase/flights";
import { agreementsPanelInnerHtml } from "./lib/agreementsUi";
import { updateComparisonPanel } from "./lib/dunleavyQ4ComparisonPanelHtml";
import { updateF720RetrospectivePanel } from "./lib/f720RetrospectiveUi";
import {
  refreshPilotCostSavedInvoices,
  wirePilotCostInvoicePanel,
} from "./lib/pilotCostInvoicePanel";
import {
  buildUgcInvoiceDataJson,
  buildUgcInvoiceDataRecord,
  buildUgcInvoicePlainText,
  buildUgcInvoiceSubject,
  effectiveInvoiceDocumentNumber,
} from "./lib/ugcInvoice";
import type { UgcInvoiceDraftInput } from "./lib/ugcInvoice";

const UGC_INVOICE_DOC_KEY = "ugc_invoice_doc";
const UGC_INVOICE_EMAIL_KEY = "ugc_invoice_to_email";
const UGC_INVOICE_REMITTER_KEY = "ugc_invoice_remitter";
/** Default “Bill to” when localStorage is empty — change in UI if Utah Gas uses another inbox. */
const UGC_INVOICE_DEFAULT_BILL_TO_EMAIL = "fd@fdunleavy.com";

/** Calendar year 2026 FET (indexed); see Rev. Proc. 2025-32 / Form 720 instructions when updated */
const defaultRates: Rates = {
  domesticSegmentUsdPerPassengerPerSegment: 5.3,
  internationalFacilityUsdPerPerson: 23.4,
  airTransportationPercentage: 0.075,
  alaskaHawaiiDomesticDepartureUsdPerPerson: 11.7,
  fuelLbsPerGallon: 6.5,
};

function readRatesFromForm(root: HTMLElement): Rates {
  const num = (id: string, fallback: number) => {
    const el = root.querySelector<HTMLInputElement>(`#${id}`);
    const v = el?.value?.trim();
    if (v === undefined || v === "") return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  return {
    domesticSegmentUsdPerPassengerPerSegment: num("r-dom", defaultRates.domesticSegmentUsdPerPassengerPerSegment),
    internationalFacilityUsdPerPerson: num("r-intl", defaultRates.internationalFacilityUsdPerPerson),
    airTransportationPercentage: num("r-pct", defaultRates.airTransportationPercentage),
    alaskaHawaiiDomesticDepartureUsdPerPerson: num("r-akhi", defaultRates.alaskaHawaiiDomesticDepartureUsdPerPerson),
    fuelLbsPerGallon: num("r-fuel", defaultRates.fuelLbsPerGallon),
  };
}

type RollupCtx = {
  isPersonalLeg: (leg: FlightLeg) => boolean;
  personalBasis: PersonalBasis;
  texasLanding?: TexasLandingOptions;
};

function readRollupCtx(root: HTMLElement): RollupCtx {
  const sel = root.querySelector<HTMLSelectElement>("#rollup-personal-basis");
  const basis: PersonalBasis = sel
    ? sel.value === "log_only"
      ? "log_only"
      : sel.value === "log_or_route_time_share"
        ? "log_or_route_time_share"
        : "notes_and_airports"
    : loadPersonalBasis();
  const inferEl = root.querySelector<HTMLInputElement>("#rollup-texas-infer");
  const inferTx = inferEl ? inferEl.checked : loadTexasInferFromDestination();
  const isPersonalLeg = personalResolverForBasis(basis);
  const txIcaoSet = texasDestinationIcaoSet();
  const texasLanding = inferTx
    ? { inferDestinationTexasIcao: txIcaoSet, inferOriginTexasIcao: txIcaoSet }
    : undefined;
  return { isPersonalLeg, personalBasis: basis, texasLanding };
}

function formatMoney(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
}

function formatFlightHours(h: number): string {
  return h.toLocaleString("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
}

const SUMMARY_QUARTER_DETAIL_KEYS = "worksheet-summary-quarters-selected";
/** Legacy single-select key; migrated once when reading. */
const SUMMARY_QUARTER_DETAIL_LEGACY = "worksheet-summary-quarter-detail";

/** Scoped quarters for Summary IRS line-item block; updated when the period report refreshes. */
let summaryScopedQuartersCache: Map<string, QuarterlyTotals> | null = null;

const PERSONAL_F720_BASIS_STORAGE = "flights_personal_f720_pct_basis_v1";

/** Optional personal transportation % model; kept for quarter checkbox handler. */
let summaryPersonalPct720Cache: {
  basis: PersonalTransport720PctBasis;
  byQuarter: Map<string, number>;
} | null = null;

/** Scoped legs + rates for re-rendering quarter detail when checkboxes change. */
let summaryQuarterDetailCtx: {
  scopedLegs: FlightLeg[];
  rates: Rates;
  isPersonalLeg: (leg: FlightLeg) => boolean;
} | null = null;

function readPersonalF720PctBasisFromForm(root: HTMLElement): PersonalTransport720PctBasis {
  const v = root.querySelector<HTMLSelectElement>("#personal-f720-pct-basis")?.value ?? "fuel2x";
  if (v === "fuel") return "fuel_cost";
  if (v === "fuel2x") return "fuel_cost_2x";
  return "off";
}

function hydratePersonalF720PctSelect(root: HTMLElement): void {
  const sel = root.querySelector<HTMLSelectElement>("#personal-f720-pct-basis");
  if (!sel) return;
  try {
    const s = sessionStorage.getItem(PERSONAL_F720_BASIS_STORAGE);
    if (s === "off" || s === "fuel" || s === "fuel2x") sel.value = s;
  } catch {
    /* ignore */
  }
}

function passengerSumForLegs(legs: FlightLeg[]): number {
  let s = 0;
  for (const l of legs) s += Math.max(0, l.passengerCount);
  return s;
}

function charterPctStats(
  legs: FlightLeg[],
  rates: Rates
): { legsWithCharter: number; passengerSum: number; taxUsd: number } {
  let legsWithCharter = 0;
  let passengerSum = 0;
  let taxUsd = 0;
  for (const leg of legs) {
    if (leg.excludeFrom720) continue;
    if (leg.charterOrFareAmountUsd <= 0) continue;
    legsWithCharter += 1;
    passengerSum += Math.max(0, leg.passengerCount);
    taxUsd = money(taxUsd + rates.airTransportationPercentage * leg.charterOrFareAmountUsd);
  }
  return { legsWithCharter, passengerSum, taxUsd };
}

function legsForQuarterKeys(
  scopedLegs: FlightLeg[],
  qMap: Map<string, QuarterlyTotals>,
  selectedKeys: string[]
): FlightLeg[] {
  const out: FlightLeg[] = [];
  for (const k of selectedKeys) {
    const qt = qMap.get(k);
    if (!qt) continue;
    out.push(
      ...filterLegsByPeriod(scopedLegs, {
        mode: "quarter",
        year: qt.year,
        quarter: qt.quarter as 1 | 2 | 3 | 4,
      })
    );
  }
  return out;
}

function partTwoMonthlyLiabilityTableHtml(
  months: MonthlyForm720Totals[],
  rates: Rates,
  quarterLegs: FlightLeg[],
  personalCtx: {
    basis: PersonalTransport720PctBasis;
    isPersonalLeg: (leg: FlightLeg) => boolean;
  } | null
): string {
  const rows = months
    .map((mo) => {
      const legsM = legsInCalendarMonth(
        quarterLegs,
        mo.calendarYear,
        mo.calendarMonth
      );
      const pPct =
        personalCtx && personalCtx.basis !== "off"
          ? personalTransportPercentageTaxTotal(legsM, rates, {
              basis: personalCtx.basis,
              isPersonalLeg: personalCtx.isPersonalLeg,
            })
          : 0;
      const combined = money(totalExciseAir(mo) + pPct);
      const label = new Date(
        mo.calendarYear,
        mo.calendarMonth - 1,
        1
      ).toLocaleString("en-US", { month: "long", year: "numeric" });
      return `<tr><td>${escapeHtml(label)}</td><td>${mo.legCount}</td><td>${formatMoney(
        combined
      )}</td></tr>`;
    })
    .join("");
  return `
        <h4 class="part-two-monthly-title">Part II — liability by month <span class="muted-small">(flight date)</span></h4>
        <p class="sheets-hint muted-small">Allocates each leg to the <strong>calendar month of <code>date</code></strong> — the usual worksheet match for a monthly liability record. Confirm the filing instructions if your liability is tied to a different event.</p>
        <table class="totals part-two-monthly-table">
          <thead><tr><th>Month</th><th>Legs</th><th>Combined air excise <span class="muted-small">(+ optional personal %)</span></th></tr></thead>
          <tbody>${rows}</tbody>
        </table>`;
}

function quarterDetailBlockHtml(
  heading: string,
  t: QuarterlyTotals,
  rates: Rates,
  legsInScope: FlightLeg[],
  personalPct720?: {
    basis: PersonalTransport720PctBasis;
    amount: number;
    stats?: PersonalTransportQuarterStats;
  },
  partTwoMonthly?: MonthlyForm720Totals[] | null,
  partTwoMonthlyPersonalCtx?: {
    basis: PersonalTransport720PctBasis;
    isPersonalLeg: (leg: FlightLeg) => boolean;
  } | null
): string {
  const h = escapeHtml(heading);
  const pPersonal =
    personalPct720 && personalPct720.basis !== "off"
      ? money(personalPct720.amount)
      : 0;
  const pctCombined = money(t.irs26PercentageTax + pPersonal);
  /** Part I line 26 — persons (7.5% + domestic segment); optional personal % rides here. */
  const form720Line26Tax = money(
    t.irs26PercentageTax + t.irs26DomesticSegmentTax + pPersonal
  );
  /** Part I line 27 — intl facility + AK/HI departure at reduced rate (IRS instructions). */
  const form720Line27Tax = money(
    t.irs27InternationalFacilityTax + t.irs26AkHiDepartureTax
  );
  const airExciseCombined = money(totalExciseAir(t) + pPersonal);
  const pctRowLabel =
    personalPct720 && personalPct720.basis !== "off"
      ? `IRS No. 26 — percentage (7.5%) — charter $ + ${escapeHtml(
          personalTransport720BasisLabel(personalPct720.basis)
        )}`
      : "IRS No. 26 — percentage (7.5% on charter $)";
  const scopeLegs = legsInScope.length;
  const scopePax = passengerSumForLegs(legsInScope);
  const charterS = charterPctStats(legsInScope, rates);
  const personalStats = personalPct720?.stats;
  const charterDetailRow =
    t.irs26PercentageTax > 0
      ? `<tr><td class="muted-small">↳ Charter $ (7.5%)</td><td class="muted-small">${charterS.legsWithCharter} leg(s) · ${charterS.passengerSum} pax (sum) · tax ${formatMoney(charterS.taxUsd)}</td></tr>`
      : "";
  const personalDetailRow =
    personalStats && personalStats.taxUsd > 0
      ? `<tr><td class="muted-small">↳ ${escapeHtml(
          personalTransport720BasisLabel(personalPct720!.basis)
        )}</td><td class="muted-small">${personalStats.legsWithBase} leg(s) · ${personalStats.passengerSumOnThoseLegs} pax (sum) · base ${formatMoney(personalStats.baseUsd)} · tax ${formatMoney(personalStats.taxUsd)}</td></tr>`
      : "";
  return `
      <div class="quarter-block">
        <h3 class="quarter-title">${h}</h3>
        <table class="totals">
          <tr><td>Legs in quarter</td><td>${scopeLegs}</td></tr>
          <tr><td>Passengers (sum of pax)</td><td>${scopePax}</td></tr>
          <tr><td>${pctRowLabel}</td><td>${formatMoney(pctCombined)}</td></tr>
          ${charterDetailRow}
          ${personalDetailRow}
          <tr><td>IRS No. 26 — domestic segment</td><td>${formatMoney(t.irs26DomesticSegmentTax)}</td></tr>
          <tr><td>IRS No. 27 — international facility</td><td>${formatMoney(t.irs27InternationalFacilityTax)}</td></tr>
          <tr><td>IRS No. 27 — Alaska/Hawaii departure <span class="muted-small">($11.40-style; same Part I line as intl)</span></td><td>${formatMoney(t.irs26AkHiDepartureTax)}</td></tr>
          <tr><td><strong>Form 720 Part I — line 26 tax</strong> <span class="muted-small">(persons)</span></td><td><strong>${formatMoney(form720Line26Tax)}</strong></td></tr>
          <tr><td><strong>Form 720 Part I — line 27 tax</strong> <span class="muted-small">(intl + AK/HI)</span></td><td><strong>${formatMoney(form720Line27Tax)}</strong></td></tr>
          <tr><td>Combined air excise</td><td>${formatMoney(airExciseCombined)}</td></tr>
          <tr><td>Fuel gallons (log)</td><td>${t.fuelGallonsFromLog.toLocaleString("en-US", { maximumFractionDigits: 2 })}</td></tr>
        </table>
        ${
          partTwoMonthly && partTwoMonthly.length === 3
            ? partTwoMonthlyLiabilityTableHtml(
                partTwoMonthly,
                rates,
                legsInScope,
                partTwoMonthlyPersonalCtx ?? null
              )
            : ""
        }
      </div>`;
}

function oneQuarterBlockHtml(
  t: QuarterlyTotals,
  scopedLegs: FlightLeg[],
  rates: Rates,
  isPersonalLeg: (leg: FlightLeg) => boolean,
  personal?: { basis: PersonalTransport720PctBasis; byQuarter: Map<string, number> }
): string {
  const k = `${t.year}-Q${t.quarter}`;
  const legsQ = filterLegsByPeriod(scopedLegs, {
    mode: "quarter",
    year: t.year,
    quarter: t.quarter as 1 | 2 | 3 | 4,
  });
  const amt = personal && personal.basis !== "off" ? personal.byQuarter.get(k) ?? 0 : 0;
  let stats: PersonalTransportQuarterStats | undefined;
  if (personal && personal.basis !== "off") {
    stats = personalTransportQuarterStats(legsQ, rates, {
      basis: personal.basis,
      isPersonalLeg,
    });
  }
  const extra =
    personal && personal.basis !== "off"
      ? { basis: personal.basis, amount: amt, stats }
      : undefined;
  const monthMap = aggregateExciseByQuarterMonths(legsQ, rates, {
    isPersonalLeg,
  });
  const partTwoMonthly = monthMap.get(k) ?? null;
  const partTwoMonthlyPersonalCtx =
    personal && personal.basis !== "off"
      ? { basis: personal.basis, isPersonalLeg }
      : null;
  return quarterDetailBlockHtml(
    `${t.year} Q${t.quarter} · ${t.legCount} legs`,
    t,
    rates,
    legsQ,
    extra,
    partTwoMonthly,
    partTwoMonthlyPersonalCtx
  );
}

function scopeTotalsRowsHtml(
  ts: TimeshareRollup,
  takeoffsAll: number,
  texasDeparturePct: number | null,
  /** All legs in this calendar quarter within Date range (personal + other); Form 720 block uses personal subset only. */
  allLegsInQuarter: number
): string {
  const f720Legs = ts.legCount;
  const excluded = Math.max(0, allLegsInQuarter - f720Legs);
  const pctF720 =
    allLegsInQuarter > 0 ? (100 * f720Legs) / allLegsInQuarter : null;
  return `
        <tr><td>Legs in Form 720 scope <span class="muted-small">(personal-classified)</span></td><td>${f720Legs}</td></tr>
        <tr><td>Legs in same quarter <span class="muted-small">(all types in Date range)</span></td><td>${allLegsInQuarter}</td></tr>
        <tr><td>Excluded from Form 720 here <span class="muted-small">(e.g. business / not personal)</span></td><td>${excluded}</td></tr>
        <tr><td>% of quarter legs in Form 720 scope</td><td>${pctF720 !== null ? `${pctF720.toFixed(1)}%` : "—"}</td></tr>
        <tr><td>Flight hours</td><td>${ts.totalHours.toFixed(2)}</td></tr>
        <tr><td>Passengers (sum of counts)</td><td>${ts.totalPassengers}</td></tr>
        <tr><td>Fuel cost (where $/gal set)</td><td>${formatMoney(ts.fuelCostTotal)}</td></tr>
        <tr><td>Personal 2× fuel → Utah Gas Corp</td><td>${formatMoney(ts.personalFuel2xTotal)}</td></tr>
        <tr><td>Texas departures <span class="muted-small">(takeoffs from TX)</span></td><td>${ts.texasDepartureTotal}</td></tr>
        <tr><td>Takeoffs <span class="muted-small">(all legs, blank → 1)</span></td><td>${takeoffsAll}</td></tr>
        <tr><td>Texas / all takeoffs</td><td>${texasDeparturePct !== null ? `${texasDeparturePct.toFixed(1)}%` : "—"}</td></tr>`;
}

function quarterDetailCombinedHtml(
  qMap: Map<string, QuarterlyTotals>,
  selectedKeys: string[],
  scopedLegs: FlightLeg[],
  rates: Rates,
  isPersonalLeg: (leg: FlightLeg) => boolean,
  personal720: { basis: PersonalTransport720PctBasis; byQuarter: Map<string, number> } | null | undefined
): string {
  const parts = selectedKeys
    .map((k) => qMap.get(k))
    .filter((x): x is QuarterlyTotals => x !== undefined);
  if (parts.length === 0) {
    return `<p class="sheets-hint">Select at least one quarter above.</p>`;
  }
  if (parts.length === 1) {
    return oneQuarterBlockHtml(
      parts[0]!,
      scopedLegs,
      rates,
      isPersonalLeg,
      personal720 ?? undefined
    );
  }
  const combined = combineQuarterlyTotals(parts);
  if (!combined) {
    return `<p class="sheets-hint">Select at least one quarter above.</p>`;
  }
  const sortedKeys = [...selectedKeys].sort((a, b) => a.localeCompare(b));
  const labels = sortedKeys
    .map((k) => {
      const qt = qMap.get(k);
      return qt ? `${qt.year} Q${qt.quarter}` : k;
    })
    .join(", ");
  const totalLegs = parts.reduce((s, q) => s + q.legCount, 0);
  let combinedPersonalAmt = 0;
  if (personal720 && personal720.basis !== "off") {
    for (const k of selectedKeys) {
      combinedPersonalAmt += personal720.byQuarter.get(k) ?? 0;
    }
    combinedPersonalAmt = money(combinedPersonalAmt);
  }
  const legsCombined = legsForQuarterKeys(scopedLegs, qMap, selectedKeys);
  let stats: PersonalTransportQuarterStats | undefined;
  if (personal720 && personal720.basis !== "off") {
    stats = personalTransportQuarterStats(legsCombined, rates, {
      basis: personal720.basis,
      isPersonalLeg,
    });
  }
  const extra =
    personal720 && personal720.basis !== "off"
      ? { basis: personal720.basis, amount: combinedPersonalAmt, stats }
      : undefined;
  return `<p class="sheets-hint muted-small">For <strong>Part II</strong> (net tax liability by month), select <strong>one</strong> quarter above — monthly rows are hidden for combined quarters.</p>${quarterDetailBlockHtml(
    `Combined (${labels}) · ${totalLegs} legs`,
    combined,
    rates,
    legsCombined,
    extra,
    null,
    null
  )}`;
}

const form720PdfFilerBase: Omit<Form720PdfFiler, "quarter_ending"> = {
  legal_name: form720FilerExample.legal_name,
  ein: form720FilerExample.ein,
  street_suite: form720FilerExample.street_suite,
  city_state_zip: form720FilerExample.city_state_zip,
};

function readSelectedQuarterKeysForF720Pdf(
  root: HTMLElement,
  validKeys: string[]
): string[] {
  const report = root.querySelector("#summary-period-report");
  let checked: string[] = [];
  if (report) {
    checked = [
      ...report.querySelectorAll<HTMLInputElement>(
        'input[name="summary-quarter-pick"]:checked'
      ),
    ]
      .map((i) => i.value)
      .filter((k) => validKeys.includes(k));
  }
  if (checked.length > 0) return checked;
  return readSummaryQuarterSelectionKeys(validKeys);
}

function readSummaryQuarterSelectionKeys(validKeys: string[]): string[] {
  if (validKeys.length === 0) return [];
  try {
    const rawMulti = sessionStorage.getItem(SUMMARY_QUARTER_DETAIL_KEYS);
    if (rawMulti) {
      const parsed = JSON.parse(rawMulti) as unknown;
      if (Array.isArray(parsed)) {
        const keys = parsed.filter(
          (x): x is string => typeof x === "string" && validKeys.includes(x)
        );
        if (keys.length > 0) return keys;
      }
    }
    const rawSingle = sessionStorage.getItem(SUMMARY_QUARTER_DETAIL_LEGACY);
    if (rawSingle && validKeys.includes(rawSingle)) return [rawSingle];
  } catch {
    /* ignore */
  }
  return [validKeys[0]!];
}

function writeSummaryQuarterSelectionKeys(keys: string[]): void {
  try {
    sessionStorage.setItem(SUMMARY_QUARTER_DETAIL_KEYS, JSON.stringify(keys));
  } catch {
    /* ignore */
  }
}

function readHondaUsdRates(root: HTMLElement): { hourly: number; landing: number } {
  const hEl = root.querySelector<HTMLInputElement>("#honda-usd-hour");
  const lEl = root.querySelector<HTMLInputElement>("#honda-usd-landing");
  const hourly = Number(hEl?.value);
  const landing = Number(lEl?.value);
  return {
    hourly:
      Number.isFinite(hourly) && hourly >= 0
        ? hourly
        : DEFAULT_HONDA_USD_PER_FLIGHT_HOUR,
    landing:
      Number.isFinite(landing) && landing >= 0
        ? landing
        : DEFAULT_HONDA_USD_PER_LANDING,
  };
}

function readHondaMonthlyUsd(root: HTMLElement): number {
  const el = root.querySelector<HTMLInputElement>("#honda-usd-monthly");
  const n = Number(el?.value);
  return Number.isFinite(n) && n >= 0 ? n : DEFAULT_HONDA_USD_MONTHLY_PROGRAM;
}

function readPeriodSelection(root: HTMLElement): PeriodSelection {
  const mode = root.querySelector<HTMLSelectElement>("#period-mode")?.value ?? "all";
  const y = parseInt(root.querySelector<HTMLInputElement>("#period-year")?.value ?? "2026", 10);
  const year = Number.isFinite(y) ? y : 2026;
  if (mode === "year") return { mode: "year", year };
  if (mode === "quarter") {
    const q = parseInt(root.querySelector<HTMLSelectElement>("#period-quarter")?.value ?? "1", 10);
    const quarter = (q >= 1 && q <= 4 ? q : 1) as 1 | 2 | 3 | 4;
    return { mode: "quarter", year, quarter };
  }
  if (mode === "month") {
    const m = parseInt(root.querySelector<HTMLSelectElement>("#period-month")?.value ?? "1", 10);
    const month = m >= 1 && m <= 12 ? m : 1;
    return { mode: "month", year, month };
  }
  if (mode === "ytd") return { mode: "ytd", year };
  return { mode: "all" };
}

/** Date range for Texas rendition / departure counts (defaults copied from Form 720 at load). */
function readTexasPeriodSelection(root: HTMLElement): PeriodSelection {
  if (!root.querySelector("#texas-period-mode")) {
    return readPeriodSelection(root);
  }
  const mode = root.querySelector<HTMLSelectElement>("#texas-period-mode")?.value ?? "all";
  const y = parseInt(
    root.querySelector<HTMLInputElement>("#texas-period-year")?.value ?? "2026",
    10
  );
  const year = Number.isFinite(y) ? y : 2026;
  if (mode === "year") return { mode: "year", year };
  if (mode === "quarter") {
    const q = parseInt(
      root.querySelector<HTMLSelectElement>("#texas-period-quarter")?.value ?? "1",
      10
    );
    const quarter = (q >= 1 && q <= 4 ? q : 1) as 1 | 2 | 3 | 4;
    return { mode: "quarter", year, quarter };
  }
  if (mode === "month") {
    const m = parseInt(
      root.querySelector<HTMLSelectElement>("#texas-period-month")?.value ?? "1",
      10
    );
    const month = m >= 1 && m <= 12 ? m : 1;
    return { mode: "month", year, month };
  }
  if (mode === "ytd") return { mode: "ytd", year };
  return { mode: "all" };
}

function copyForm720PeriodToTexasPanel(root: HTMLElement): void {
  const m = root.querySelector<HTMLSelectElement>("#period-mode");
  const tm = root.querySelector<HTMLSelectElement>("#texas-period-mode");
  if (!m || !tm) return;
  tm.value = m.value;
  const y = root.querySelector<HTMLInputElement>("#period-year");
  const ty = root.querySelector<HTMLInputElement>("#texas-period-year");
  if (y && ty) ty.value = y.value;
  const q = root.querySelector<HTMLSelectElement>("#period-quarter");
  const tq = root.querySelector<HTMLSelectElement>("#texas-period-quarter");
  if (q && tq) tq.value = q.value;
  const mo = root.querySelector<HTMLSelectElement>("#period-month");
  const tmo = root.querySelector<HTMLSelectElement>("#texas-period-month");
  if (mo && tmo) tmo.value = mo.value;
}

function syncTexasPeriodBarVisibility(root: HTMLElement): void {
  const mode = root.querySelector<HTMLSelectElement>("#texas-period-mode")?.value ?? "all";
  const yearG = root.querySelector<HTMLElement>(".texas-period-group-year");
  const quarterG = root.querySelector<HTMLElement>(".texas-period-group-quarter");
  const monthG = root.querySelector<HTMLElement>(".texas-period-group-month");
  if (!yearG || !quarterG || !monthG) return;
  yearG.classList.toggle("hidden", mode === "all");
  quarterG.classList.toggle("hidden", mode !== "quarter");
  monthG.classList.toggle("hidden", mode !== "month");
}

function formatPeriodRangeLabel(sel: PeriodSelection): string {
  if (sel.mode === "all") {
    return "All time (flat fee = distinct calendar months that have legs)";
  }
  if (sel.mode === "year") return `Calendar year ${sel.year}`;
  if (sel.mode === "month") {
    return `${sel.year}-${String(sel.month).padStart(2, "0")}`;
  }
  if (sel.mode === "quarter") return `${sel.year} Q${sel.quarter}`;
  if (sel.mode === "ytd") {
    return `Year to date (${sel.year}) through ${toIsoDateLocal(new Date())}`;
  }
  if (sel.mode === "janThroughMonth") {
    return `Jan 1–${lastIsoDayOfCalendarMonth(sel.year, sel.month)} (${sel.year})`;
  }
  return "";
}

function readHondaMaintenanceSnapshotForExport(root: HTMLElement): HondaMaintenanceSnapshot {
  const { hourly, landing } = readHondaUsdRates(root);
  return hondaMaintenanceFromForm(root, readHondaMonthlyUsd(root), hourly, landing);
}

function flatFeeMonthsCaption(sel: PeriodSelection, months: number): string {
  if (sel.mode === "all") {
    return `${months} month(s) with legs`;
  }
  if (sel.mode === "year") return "12 calendar months";
  if (sel.mode === "quarter") return "3 calendar months";
  if (sel.mode === "month") return "1 calendar month";
  if (sel.mode === "ytd") {
    if (months === 0) return "no months elapsed (future year)";
    return `${months} month(s) Jan–through today`;
  }
  if (sel.mode === "janThroughMonth") {
    const long = new Date(sel.year, sel.month - 1, 1).toLocaleString("en-US", {
      month: "long",
    });
    return `${sel.month} calendar month(s) (Jan–${long} ${sel.year})`;
  }
  return "";
}

type SummaryHorizon = { label: string; sel: PeriodSelection };

/** Calendar buckets for Summary cost tables (independent of Date range scoping). */
function summaryCalendarHorizons(asOf = new Date()): SummaryHorizon[] {
  const y = asOf.getFullYear();
  const m = asOf.getMonth() + 1;
  const q = (Math.floor((m - 1) / 3) + 1) as 1 | 2 | 3 | 4;
  return [
    { label: "This month", sel: { mode: "month", year: y, month: m } },
    { label: "This quarter", sel: { mode: "quarter", year: y, quarter: q } },
    { label: "Year to date", sel: { mode: "ytd", year: y } },
    { label: "All time", sel: { mode: "all" } },
  ];
}

function readMaintSummaryAnchor(root: HTMLElement): { year: number; month: number } {
  const yEl = root.querySelector<HTMLInputElement>("#maint-summary-year");
  const mEl = root.querySelector<HTMLSelectElement>("#maint-summary-month");
  const y = parseInt(yEl?.value ?? "", 10);
  const m = parseInt(mEl?.value ?? "", 10);
  const now = new Date();
  const year = Number.isFinite(y) && y >= 2000 && y <= 2100 ? y : now.getFullYear();
  const month = Number.isFinite(m) && m >= 1 && m <= 12 ? m : now.getMonth() + 1;
  return { year, month };
}

/** Maintenance · summary columns — anchored to selected year/month (not device calendar). */
function maintenanceSummaryHorizons(root: HTMLElement): SummaryHorizon[] {
  const { year, month } = readMaintSummaryAnchor(root);
  const q = (Math.floor((month - 1) / 3) + 1) as 1 | 2 | 3 | 4;
  const monthShort = new Date(year, month - 1, 1).toLocaleString("en-US", {
    month: "short",
    year: "numeric",
  });
  const throughShort = new Date(year, month - 1, 1).toLocaleString("en-US", {
    month: "short",
  });
  return [
    { label: monthShort, sel: { mode: "month", year, month } },
    { label: `${year} Q${q}`, sel: { mode: "quarter", year, quarter: q } },
    {
      label: `Jan–${throughShort} ${year}`,
      sel: { mode: "janThroughMonth", year, month },
    },
    { label: "All time", sel: { mode: "all" } },
  ];
}

/** Explains why Scope A “this month / quarter” can be $0 while YTD is not (calendar Q vs YTD). */
function scopeACalendarBucketsExplainHtml(asOfLocalIso: string): string {
  const [ys, ms] = asOfLocalIso.split("-");
  const y = parseInt(ys ?? "", 10);
  const m = parseInt(ms ?? "", 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return "";
  const q = (Math.floor((m - 1) / 3) + 1) as 1 | 2 | 3 | 4;
  const monthLong = new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long" });
  const qRange =
    q === 1 ? "Jan–Mar" : q === 2 ? "Apr–Jun" : q === 3 ? "Jul–Sep" : "Oct–Dec";
  return `<p class="sheets-hint muted-small ugc-scope-a-calendar-hint" role="note">Uses <strong>this device’s calendar</strong> as of ${escapeHtml(asOfLocalIso)}: <strong>This month</strong> = ${escapeHtml(monthLong)} ${escapeHtml(String(y))} only. <strong>This quarter</strong> = ${escapeHtml(String(y))} Q${q} (${escapeHtml(qRange)}). Early in a new month or quarter those rows are often <strong>$0</strong> while <strong>Year to date</strong> still includes personal trips from earlier in the year — that is expected, not a bug.</p>`;
}

type HorizonCostBreakdown = {
  hondaFlat: number;
  hondaHourly: number;
  hondaLanding: number;
  fuelCost: number;
  ugcPersonalOwed: number;
  /** Personal-classified legs in this horizon (same scope as <code>ugcPersonalOwed</code>). */
  ugcPersonalLegs: number;
  /** Sum of <code>passengerCount</code> on those legs. */
  ugcPersonalPaxSum: number;
  directOperatingTotal: number;
  maintenanceTotal: number;
  totalFlightHours: number;
};

function computeHorizonCostBreakdown(
  legs: FlightLeg[],
  rates: Rates,
  sel: PeriodSelection,
  monthlyUsd: number,
  hourlyUsd: number,
  landingUsd: number,
  rollup: RollupCtx
): HorizonCostBreakdown {
  const { flatTotal } = flatFeeMonthsInPeriod(legs, sel, monthlyUsd);
  const fl = filterLegsByPeriod(legs, sel);
  const ts = timeshareRollup(fl, rates, {
    isPersonalLeg: rollup.isPersonalLeg,
    texasLanding: rollup.texasLanding,
  });
  const s = summarizeCompanyHonda(fl, rates, hourlyUsd, landingUsd, {
    isPersonalLeg: rollup.isPersonalLeg,
    texasLanding: rollup.texasLanding,
  });
  const ugcPersonalOwed = money(
    s.personalAirExciseReimburseTotal + s.personalFuelOwed2xTotal
  );
  const maintenanceTotal = money(flatTotal + s.hondaHourlyCharge + s.hondaLandingCharge);
  const directOperatingTotal = money(
    maintenanceTotal + ts.fuelCostTotal + ugcPersonalOwed
  );
  return {
    hondaFlat: flatTotal,
    hondaHourly: s.hondaHourlyCharge,
    hondaLanding: s.hondaLandingCharge,
    fuelCost: ts.fuelCostTotal,
    ugcPersonalOwed,
    ugcPersonalLegs: s.tripsPersonal,
    ugcPersonalPaxSum: s.personalPassengerCountSum,
    directOperatingTotal,
    maintenanceTotal,
    totalFlightHours: ts.totalHours,
  };
}

/** Honda hourly/landing + fuel + Utah Gas personal on these legs only — no program flat fee. */
type SubsetDirectOpBreakdown = {
  hondaHourly: number;
  hondaLanding: number;
  fuelCost: number;
  ugcPersonalOwed: number;
  ugcPersonalLegs: number;
  ugcPersonalPaxSum: number;
  directOperatingTotal: number;
  totalFlightHours: number;
};

function computeSubsetDirectOpBreakdown(
  subset: FlightLeg[],
  rates: Rates,
  hourlyUsd: number,
  landingUsd: number,
  rollup: RollupCtx
): SubsetDirectOpBreakdown {
  const ts = timeshareRollup(subset, rates, {
    isPersonalLeg: rollup.isPersonalLeg,
    texasLanding: rollup.texasLanding,
  });
  const s = summarizeCompanyHonda(subset, rates, hourlyUsd, landingUsd, {
    isPersonalLeg: rollup.isPersonalLeg,
    texasLanding: rollup.texasLanding,
  });
  const ugcPersonalOwed = money(
    s.personalAirExciseReimburseTotal + s.personalFuelOwed2xTotal
  );
  const directOperatingTotal = money(
    s.hondaHourlyCharge + s.hondaLandingCharge + ts.fuelCostTotal + ugcPersonalOwed
  );
  return {
    hondaHourly: s.hondaHourlyCharge,
    hondaLanding: s.hondaLandingCharge,
    fuelCost: ts.fuelCostTotal,
    ugcPersonalOwed,
    ugcPersonalLegs: s.tripsPersonal,
    ugcPersonalPaxSum: s.personalPassengerCountSum,
    directOperatingTotal,
    totalFlightHours: ts.totalHours,
  };
}

function updateSummaryCostOverviewPanels(
  root: HTMLElement,
  legs: FlightLeg[],
  rates: Rates,
  err: string | null
): void {
  const directEl = root.querySelector("#summary-direct-op-panel");
  const maintEl = root.querySelector("#summary-maintenance-overview-panel");
  if (!directEl || !maintEl) return;

  if (err) {
    directEl.innerHTML = "";
    maintEl.innerHTML = "";
    return;
  }

  const monthlyUsd = readHondaMonthlyUsd(root);
  const { hourly, landing } = readHondaUsdRates(root);
  const horizons = summaryCalendarHorizons();
  const rollup = readRollupCtx(root);
  const rows = horizons.map(({ sel }) =>
    computeHorizonCostBreakdown(legs, rates, sel, monthlyUsd, hourly, landing, rollup)
  );
  const maintHorizons = maintenanceSummaryHorizons(root);
  const maintRows = maintHorizons.map(({ sel }) =>
    computeHorizonCostBreakdown(legs, rates, sel, monthlyUsd, hourly, landing, rollup)
  );

  const asOf = toIsoDateLocal(new Date());
  const headerCols = horizons
    .map((h) => `<th scope="col">${escapeHtml(h.label)}</th>`)
    .join("");
  const headerRecent =
    '<th scope="col">Last trip</th><th scope="col">Last 10 trips</th>';

  const last1 = pickLastNLegsByDate(legs, 1);
  const last10 = pickLastNLegsByDate(legs, 10);
  const rb1 = computeSubsetDirectOpBreakdown(last1, rates, hourly, landing, rollup);
  const rb10 = computeSubsetDirectOpBreakdown(last10, rates, hourly, landing, rollup);

  const tdDash = () =>
    '<td class="summary-cost-num"><span class="muted-small">—</span></td>';
  const tdMoney = (n: number) =>
    `<td class="summary-cost-num">${formatMoney(n)}</td>`;
  const vendorsDirectTotal = (r: HorizonCostBreakdown) =>
    money(r.directOperatingTotal - r.ugcPersonalOwed);
  const tdHoursVal = (h: number) =>
    `<td class="summary-cost-num">${
      h > 0 ? formatFlightHours(h) : '<span class="muted-small">—</span>'
    }</td>`;
  const subsetVendorsDirect = (b: SubsetDirectOpBreakdown) =>
    money(b.directOperatingTotal - b.ugcPersonalOwed);
  const tdPerHour = (b: SubsetDirectOpBreakdown) =>
    `<td class="summary-cost-num">${
      b.totalFlightHours > 0
        ? formatMoney(subsetVendorsDirect(b) / b.totalFlightHours)
        : '<span class="muted-small">—</span>'
    }</td>`;

  const calMoneyCells = (get: (r: HorizonCostBreakdown) => number) =>
    rows
      .map((r) => `<td class="summary-cost-num">${formatMoney(get(r))}</td>`)
      .join("");

  const calHoursCells = (get: (r: HorizonCostBreakdown) => number) =>
    rows
      .map((r) => {
        const h = get(r);
        const t =
          h > 0 ? formatFlightHours(h) : '<span class="muted-small">—</span>';
        return `<td class="summary-cost-num">${t}</td>`;
      })
      .join("");

  const calPerHourCells = () =>
    rows
      .map((r) => {
        const t =
          r.totalFlightHours > 0
            ? formatMoney(vendorsDirectTotal(r) / r.totalFlightHours)
            : '<span class="muted-small">—</span>';
        return `<td class="summary-cost-num">${t}</td>`;
      })
      .join("");

  const trDirect = (
    labelHtml: string,
    calHtml: string,
    r1: string,
    r10: string,
    totalRow = false
  ) => {
    const trClass = totalRow ? ' class="summary-cost-total-row"' : "";
    return `<tr${trClass}><th scope="row">${labelHtml}</th>${calHtml}${r1}${r10}</tr>`;
  };

  const maintHeaderCols = maintHorizons
    .map((h) => `<th scope="col">${escapeHtml(h.label)}</th>`)
    .join("");
  const rowMoneyMaint = (
    labelHtml: string,
    get: (r: HorizonCostBreakdown) => number,
    totalRow = false
  ) => {
    const cells = maintRows
      .map((r) => `<td class="summary-cost-num">${formatMoney(get(r))}</td>`)
      .join("");
    const trClass = totalRow ? ' class="summary-cost-total-row"' : "";
    return `<tr${trClass}><th scope="row">${labelHtml}</th>${cells}</tr>`;
  };

  directEl.innerHTML = `
    <section class="panel summary-cost-section">
      <h2>Direct operating costs</h2>
      <p class="sheets-hint summary-cost-lead">Recurring operating charges tied to aircraft use: <strong>Honda program</strong> (flat, hourly, per-landing) and <strong>fuel cost</strong> from the log where $/gal is set. Personal reimbursement to Utah Gas (air excise + 2× fuel) stays on the <button type="button" class="tab-jump-link" data-open-main-tab="ugc">Timeshare</button> tab.</p>
      <p class="sheets-hint muted-small summary-cost-note">First columns use this device’s calendar: current month, quarter, year-to-date through ${escapeHtml(asOf)}, and all loaded legs. <strong>Last trip</strong> and <strong>Last 10 trips</strong> are the newest leg(s) by date (hourly, per-landing, and fuel on those legs only — program flat fee shown as —). This grid is <em>not</em> the Form 720 date picker (use the Form 720 tab).</p>
      <div class="table-scroll">
        <table class="totals summary-cost-table">
          <thead>
            <tr><th scope="col" class="summary-cost-stub"></th>${headerCols}${headerRecent}</tr>
          </thead>
          <tbody>
            ${trDirect(
              "Honda program flat fee",
              calMoneyCells((r) => r.hondaFlat),
              tdDash(),
              tdDash()
            )}
            ${trDirect(
              "Honda hourly charge",
              calMoneyCells((r) => r.hondaHourly),
              tdMoney(rb1.hondaHourly),
              tdMoney(rb10.hondaHourly)
            )}
            ${trDirect(
              "Honda per-landing (cycle) charge",
              calMoneyCells((r) => r.hondaLanding),
              tdMoney(rb1.hondaLanding),
              tdMoney(rb10.hondaLanding)
            )}
            ${trDirect(
              'Fuel cost <span class="muted-small">(where $/gal set)</span>',
              calMoneyCells((r) => r.fuelCost),
              tdMoney(rb1.fuelCost),
              tdMoney(rb10.fuelCost)
            )}
            ${trDirect(
              "Direct operating cost total",
              calMoneyCells((r) => vendorsDirectTotal(r)),
              tdMoney(subsetVendorsDirect(rb1)),
              tdMoney(subsetVendorsDirect(rb10)),
              true
            )}
            ${trDirect(
              "Flight hours (sum)",
              calHoursCells((r) => r.totalFlightHours),
              tdHoursVal(rb1.totalFlightHours),
              tdHoursVal(rb10.totalFlightHours)
            )}
            ${trDirect(
              'Direct operating <span class="muted-small">$ / flight hour</span>',
              calPerHourCells(),
              tdPerHour(rb1),
              tdPerHour(rb10),
              true
            )}
          </tbody>
        </table>
      </div>
    </section>
  `;

  maintEl.innerHTML = `
    <section class="panel summary-cost-section">
      <h2>Maintenance · summary</h2>
      <p class="sheets-hint summary-cost-lead">Maintenance-related Honda charges only: flat fee, hourly, and per-landing (cycle). The first three columns follow <strong>Summary — year / month</strong> above (that month, its calendar quarter, Jan through that month, then all loaded legs). <strong>Form 720 → Date range</strong> only scopes the program rollups and company metrics below — not this grid.</p>
      <div class="table-scroll">
        <table class="totals summary-cost-table">
          <thead>
            <tr><th scope="col" class="summary-cost-stub"></th>${maintHeaderCols}</tr>
          </thead>
          <tbody>
            ${rowMoneyMaint("Honda program flat fee", (r) => r.hondaFlat)}
            ${rowMoneyMaint("Honda hourly charge", (r) => r.hondaHourly)}
            ${rowMoneyMaint("Honda per-landing (cycle) charge", (r) => r.hondaLanding)}
            ${rowMoneyMaint("Total maintenance-related cost", (r) => r.maintenanceTotal, true)}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

function syncPeriodBarVisibility(root: HTMLElement): void {
  const mode = root.querySelector<HTMLSelectElement>("#period-mode")?.value ?? "all";
  const yearG = root.querySelector<HTMLElement>(".period-group-year");
  const quarterG = root.querySelector<HTMLElement>(".period-group-quarter");
  const monthG = root.querySelector<HTMLElement>(".period-group-month");
  if (!yearG || !quarterG || !monthG) return;
  yearG.classList.toggle("hidden", mode === "all");
  quarterG.classList.toggle("hidden", mode !== "quarter");
  monthG.classList.toggle("hidden", mode !== "month");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Data → Flight legs: domestic segment pax (dom_pax or passenger_count) + intl + AK/HI — matches Form 720 headcounts. */
function dataTableExcisePassengerTotal(leg: FlightLeg): number {
  return (
    effectiveDomesticSegmentPassengers(leg) +
    effectiveInternationalFacilityPassengers(leg) +
    effectiveAlaskaHawaiiDeparturePassengers(leg)
  );
}

function personalBasisLabel(b: PersonalBasis): string {
  if (b === "log_only") return "Log / CSV personal column only";
  if (b === "log_or_route_time_share") {
    return "Log column or “time share” in route notes only";
  }
  return "Notes + passengers + airports (Grand Junction, keywords, time share)";
}

/** Explains $0 payment lines (rollup mode, date scope, or truly zero tax/fuel). */
function personalPaymentHints(basis: PersonalBasis, s: CompanyHondaSummary): string {
  const parts: string[] = [];
  if (basis === "log_only") {
    parts.push(
      `<p class="sheets-hint" role="note">Rollup is <strong>Log / CSV personal column only</strong> (saved in this browser from an older session). Personal vs business ignores time share wording in notes. Clear this site’s stored data to return to the default <strong>notes + passengers + airports</strong> rules.</p>`
    );
  } else if (basis === "log_or_route_time_share") {
    parts.push(
      `<p class="sheets-hint muted-small">Rollup is <strong>log personal flag and/or time share in route notes only</strong> (saved in this browser). Clear this site’s stored data to return to the default <strong>notes + passengers + airports</strong> rules.</p>`
    );
  }
  if (s.tripsPersonal === 0 && basis !== "log_only") {
    if (s.tripsTotal === 0) {
      parts.push(
        `<p class="sheets-hint muted-small"><strong>No flight legs</strong> in the worksheet for this date scope — reimbursement stays at <strong>$0.00</strong> until you add trips (<strong>Manual Input</strong>, <strong>Bulk CSV</strong>, or <strong>Refresh from Supabase</strong> when the backend is configured). This is not a tax-rate issue.</p>`
      );
    } else {
      parts.push(
        `<p class="sheets-hint muted-small">No legs classified as <strong>personal</strong> in this date scope (${s.tripsTotal} trip(s) total). Check <strong>Maintenance → Business vs personal</strong> rollup mode, the log <code>personal</code> column, and <strong>“time share”</strong> in route/passenger notes — or widen <strong>Form 720 → Date range</strong> if trips fall outside it.</p>`
      );
    }
  }
  if (
    s.tripsPersonal > 0 &&
    s.personalAirExciseReimburseTotal === 0 &&
    s.personalFuelOwed2xTotal === 0
  ) {
    parts.push(
      `<p class="sheets-hint muted-small">${s.tripsPersonal} personal trip(s) in scope, but IRS air excise is zero on those rows (or skip) and/or 2× fuel has no usable price per gallon on the leg (common on older imports even when fuel weight exists).</p>`
    );
  }
  return parts.join("");
}

function companyMetricsHtml(
  s: CompanyHondaSummary,
  hourly: number,
  landing: number,
  periodLabel: string,
  personalBasis: PersonalBasis
): string {
  const pctTrips =
    s.pctTripsBusiness === null ? "—" : `${s.pctTripsBusiness.toFixed(1)}%`;
  const pctHrs =
    s.pctHoursBusiness === null
      ? "— <span class=\"muted-small\">(enter flight hours on legs)</span>"
      : `${s.pctHoursBusiness.toFixed(1)}%`;
  const periodEsc = escapeHtml(periodLabel);
  const basisEsc = escapeHtml(personalBasisLabel(personalBasis));

  return `
    <p class="sheets-hint company-period-banner"><strong>Date scope</strong> (same as <strong>Form 720 → Date range</strong>): ${periodEsc}. Every number below sums legs whose <code>date</code> falls in this range (change the range on Form 720 and totals refresh automatically).</p>
    <div class="company-grid">
      <div class="company-block">
        <h3 class="company-block-title">Takeoffs</h3>
        <table class="totals company-mini">
          <tr><td>Total takeoffs</td><td>${s.totalTakeoffs}</td></tr>
          <tr><td>Takeoffs departing <strong>TX</strong> <span class="muted-small">(<code>departure_state</code> = TX, or <code>from</code> ICAO in TX list when Form 720 <strong>Infer Texas…</strong> is on)</span></td><td>${s.takeoffsTexas}</td></tr>
        </table>
      </div>
      <div class="company-block">
        <h3 class="company-block-title">Business vs personal</h3>
        <p class="sheets-hint muted-small rollup-mode-chip">Active rollup: <strong>${basisEsc}</strong> <span class="muted-small">(saved in this browser if you used an older build with rollup options)</span></p>
        <p class="sheets-hint company-explain">Uses the same rules as the worksheet rollup (log <code>personal</code>, combined <strong>route + passenger</strong> text, <strong>Grand Junction (KGJT)</strong> as a business anchor, time share wording, and other note keywords—or narrower modes if still stored from an older session). There is no auto “50% rule”—if a trip is mixed, split into rows or pick a classification with your advisor.</p>
        <table class="totals company-mini">
          <tr><td>Trips — business</td><td>${s.tripsBusiness}</td></tr>
          <tr><td>Trips — personal</td><td>${s.tripsPersonal}</td></tr>
          <tr><td>% of trips that are business</td><td>${pctTrips}</td></tr>
          <tr><td>Flight hours — business</td><td>${s.hoursBusiness.toFixed(2)}</td></tr>
          <tr><td>Flight hours — personal</td><td>${s.hoursPersonal.toFixed(2)}</td></tr>
          <tr><td>% of <em>recorded</em> hours that are business</td><td>${pctHrs}</td></tr>
        </table>
      </div>
    </div>
    <div class="company-block">
      <h3 class="company-block-title">Program variable (${DEFAULT_AIRCRAFT_ID})</h3>
      <p class="sheets-hint"><strong>Maintenance</strong> tab rates × totals for <strong>${periodEsc}</strong>: <strong>${formatMoney(hourly)}</strong> × flight hours below + <strong>${formatMoney(landing)}</strong> × landings (cycles) below.</p>
      <table class="totals company-mini">
        <tr><td>Total flight hours (sum of legs)</td><td>${s.totalFlightHours.toFixed(2)}</td></tr>
        <tr><td>Hour charge (${formatMoney(hourly)} × hours)</td><td>${formatMoney(s.hondaHourlyCharge)}</td></tr>
        <tr><td>Landings (cycles) <span class="muted-small">(blank defaults to 1 per leg)</span></td><td>${s.totalLandings}</td></tr>
        <tr><td>Landing / cycle charge (${formatMoney(landing)} × landings)</td><td>${formatMoney(s.hondaLandingCharge)}</td></tr>
        <tr><td><strong>Variable subtotal</strong></td><td><strong>${formatMoney(s.hondaVariableSubtotal)}</strong></td></tr>
      </table>
    </div>
    <div class="company-block">
      <h3 class="company-block-title">Personal → Utah Gas Corp</h3>
      <p class="sheets-hint muted-small">This <strong>Maintenance</strong> screen follows <strong>Form 720 → Date range</strong> only. For reimbursement dollars see <strong>Timeshare</strong>: <em>Scope B</em> matches this date range; <em>Scope A</em> is the separate calendar-month / quarter / YTD table.</p>
    </div>
  `;
}

function updateCompanyPanel(
  root: HTMLElement,
  legs: FlightLeg[],
  rates: Rates,
  err: string | null
): void {
  const el = root.querySelector("#company-metrics");
  if (!el) return;
  if (err) {
    el.innerHTML = `<p class="error" role="alert">${escapeHtml(err)}</p>`;
    return;
  }
  const rollup = readRollupCtx(root);
  const sel = readPeriodSelection(root);
  const fl = filterLegsByPeriod(legs, sel);
  const { hourly, landing } = readHondaUsdRates(root);
  const s = summarizeCompanyHonda(fl, rates, hourly, landing, {
    isPersonalLeg: rollup.isPersonalLeg,
    texasLanding: rollup.texasLanding,
  });
  const cloud = hasSupabaseConfig();
  const dataNote = cloud
    ? `<p class="sheets-hint muted-small">These metrics use <strong>all worksheet legs</strong>, including <code>flight_legs</code> from Supabase after load or <strong>Refresh from Supabase</strong> / <strong>Data → Flight legs</strong>. With no rows loaded, values stay at zero.</p>`
    : `<p class="sheets-hint muted-small">Connect Supabase to load <code>flight_legs</code> into the worksheet; until then use Manual Input (and Bulk CSV on desktop).</p>`;
  el.innerHTML =
    dataNote +
    companyMetricsHtml(
      s,
      hourly,
      landing,
      formatPeriodRangeLabel(sel),
      rollup.personalBasis
    );
}

function hydrateUgcInvoiceStoredFields(root: HTMLElement): void {
  try {
    const doc = root.querySelector<HTMLInputElement>("#ugc-invoice-doc");
    const em = root.querySelector<HTMLInputElement>("#ugc-invoice-to-email");
    const rem = root.querySelector<HTMLInputElement>("#ugc-invoice-remitter");
    const dt = root.querySelector<HTMLInputElement>("#ugc-invoice-date");
    if (doc && !doc.dataset.hydrated) {
      const v = localStorage.getItem(UGC_INVOICE_DOC_KEY);
      if (v) doc.value = v;
      doc.dataset.hydrated = "1";
    }
    if (em && !em.dataset.hydrated) {
      const v = localStorage.getItem(UGC_INVOICE_EMAIL_KEY);
      em.value = (v ?? "").trim() || UGC_INVOICE_DEFAULT_BILL_TO_EMAIL;
      em.dataset.hydrated = "1";
    }
    if (rem && !rem.dataset.hydrated) {
      const v = localStorage.getItem(UGC_INVOICE_REMITTER_KEY);
      rem.value = v || "Francis Dunleavy (time-sharee)";
      rem.dataset.hydrated = "1";
    }
    if (dt && !dt.value) {
      dt.value = toIsoDateLocal(new Date());
    }
  } catch {
    /* ignore */
  }
}

function personalBasisShortForInvoice(b: RollupCtx["personalBasis"]): string {
  return b === "log_only"
    ? "log personal column"
    : b === "log_or_route_time_share"
      ? "log + time share in notes"
      : "notes + passengers + airports";
}

function buildUgcInvoiceDraftInputFromWorksheet(
  root: HTMLElement,
  legs: FlightLeg[],
  rates: Rates
): UgcInvoiceDraftInput {
  const rollup = readRollupCtx(root);
  const f720Sel = readPeriodSelection(root);
  const f720Legs = filterLegsByPeriod(legs, f720Sel);
  const { hourly, landing } = readHondaUsdRates(root);
  const s = summarizeCompanyHonda(f720Legs, rates, hourly, landing, {
    isPersonalLeg: rollup.isPersonalLeg,
    texasLanding: rollup.texasLanding,
  });
  const docEl = root.querySelector<HTMLInputElement>("#ugc-invoice-doc");
  const dateEl = root.querySelector<HTMLInputElement>("#ugc-invoice-date");
  const remEl = root.querySelector<HTMLInputElement>("#ugc-invoice-remitter");
  const issuedDateIso = dateEl?.value?.trim() || toIsoDateLocal(new Date());
  return {
    aircraftId: DEFAULT_AIRCRAFT_ID,
    periodLabel: formatPeriodRangeLabel(f720Sel),
    invoiceDocumentNumber: docEl?.value ?? "",
    issuedDateIso,
    personalBasisLabel: personalBasisShortForInvoice(rollup.personalBasis),
    personalTripsInScope: s.tripsPersonal,
    legsInScopeTotal: f720Legs.length,
    airExciseReimburse: s.personalAirExciseReimburseTotal,
    fuel2x: s.personalFuelOwed2xTotal,
    remitterLine: (remEl?.value ?? "").trim() || "Time-sharee",
  };
}

function updateUgcInvoicePanel(
  root: HTMLElement,
  legs: FlightLeg[],
  rates: Rates,
  err: string | null
): void {
  const summary = root.querySelector("#ugc-invoice-summary");
  const ta = root.querySelector<HTMLTextAreaElement>("#ugc-invoice-body");
  if (!summary) return;
  hydrateUgcInvoiceStoredFields(root);

  if (err) {
    summary.innerHTML = `<p class="error" role="alert">${escapeHtml(err)}</p>`;
    return;
  }

  const draftIn = buildUgcInvoiceDraftInputFromWorksheet(root, legs, rates);
  const air = draftIn.airExciseReimburse;
  const fuel = draftIn.fuel2x;
  const total = money(air + fuel);
  const periodEsc = escapeHtml(draftIn.periodLabel);

  summary.innerHTML = `
    <p class="sheets-hint company-period-banner"><strong>Scope B — same as Timeshare / Form 720:</strong> ${periodEsc}</p>
    <table class="totals company-mini ugc-invoice-mini">
      <tr><td>Legs in this date range (all)</td><td><strong>${draftIn.legsInScopeTotal}</strong></td></tr>
      <tr><td>Personal trips in range</td><td><strong>${draftIn.personalTripsInScope}</strong></td></tr>
      <tr><td>Personal — air excise (IRS 26+27)</td><td><strong>${formatMoney(air)}</strong></td></tr>
      <tr><td>Personal — 2× fuel</td><td><strong>${formatMoney(fuel)}</strong></td></tr>
      <tr><td><strong>Total due to Utah Gas Corp</strong></td><td><strong>${formatMoney(total)}</strong></td></tr>
    </table>
  `;

  if (ta && ta.dataset.userEdited !== "1") {
    ta.value = buildUgcInvoicePlainText(draftIn);
  }
}

function updateUtahGasPersonalPanel(
  root: HTMLElement,
  legs: FlightLeg[],
  rates: Rates,
  err: string | null
): void {
  const el = root.querySelector("#ugc-personal-panel");
  if (!el) return;
  if (err) {
    el.innerHTML = `<section class="panel"><p class="error" role="alert">${escapeHtml(err)}</p></section>`;
    return;
  }
  const rollup = readRollupCtx(root);
  const { hourly, landing } = readHondaUsdRates(root);
  const horizons = summaryCalendarHorizons();
  const asOf = toIsoDateLocal(new Date());
  const basisEsc = escapeHtml(personalBasisLabel(rollup.personalBasis));

  const horizonRows = horizons
    .map(({ label, sel }) => {
      const fl = filterLegsByPeriod(legs, sel);
      const s = summarizeCompanyHonda(fl, rates, hourly, landing, {
        isPersonalLeg: rollup.isPersonalLeg,
        texasLanding: rollup.texasLanding,
      });
      const total = money(
        s.personalAirExciseReimburseTotal + s.personalFuelOwed2xTotal
      );
      const legWord = s.tripsPersonal === 1 ? "leg" : "legs";
      const paxMeta = `<span class="muted-small"> · ${s.tripsPersonal} ${legWord} · ${s.personalPassengerCountSum} pax</span>`;
      return `<tr>
        <th scope="row">${escapeHtml(label)}</th>
        <td>${s.tripsPersonal}</td>
        <td class="summary-cost-num">${formatMoney(s.personalAirExciseReimburseTotal)}${paxMeta}</td>
        <td class="summary-cost-num">${formatMoney(s.personalFuelOwed2xTotal)}${paxMeta}</td>
        <td class="summary-cost-num"><strong>${formatMoney(total)}</strong>${paxMeta}</td>
      </tr>`;
    })
    .join("");

  const f720Sel = readPeriodSelection(root);
  const f720Legs = filterLegsByPeriod(legs, f720Sel);
  const f720S = summarizeCompanyHonda(f720Legs, rates, hourly, landing, {
    isPersonalLeg: rollup.isPersonalLeg,
    texasLanding: rollup.texasLanding,
  });
  const f720Total = money(
    f720S.personalAirExciseReimburseTotal + f720S.personalFuelOwed2xTotal
  );
  const f720LegMeta = `<span class="muted-small"> · ${f720S.tripsPersonal} leg${
    f720S.tripsPersonal === 1 ? "" : "s"
  } · ${f720S.personalPassengerCountSum} pax</span>`;
  const periodEsc = escapeHtml(formatPeriodRangeLabel(f720Sel));
  const cloud = hasSupabaseConfig();
  const legCountHint =
    legs.length === 0
      ? "<strong>No legs in the worksheet yet</strong> — tables below show zeros until you add flights or load from the cloud."
      : `<strong>${legs.length}</strong> leg(s) in the worksheet.`;

  el.innerHTML = `
    <section class="panel ugc-personal-intro">
      <h2 class="tab-panel-heading">Time sharing agreement — reimbursement</h2>
      <p class="panel-lead">Estimates <strong>time-sharee</strong> amounts owed to <strong>Utah Gas Corp</strong> (time sharor) on <strong>personal</strong> legs under the <strong>aircraft time sharing agreement</strong> for <strong>${DEFAULT_AIRCRAFT_ID}</strong>: allocable <strong>IRS Nos. 26+27</strong> air excise, plus <strong>2× fuel</strong> (agreement §2.3(a)+(j) style) when gallons and $/gal are on the leg.</p>
      <p class="sheets-hint muted-small">If you only need <strong>federal quarter excise</strong> for all legs (business + personal), use <button type="button" class="tab-jump-link" data-open-main-tab="f720">Form 720</button> — this tab is for <strong>personal reimbursement</strong> and two date scopes.</p>
      <p class="sheets-hint">${legCountHint} ${
        cloud
          ? "Totals use <code>flight_legs</code> once they are loaded into the worksheet (open app, <strong>Refresh from Supabase</strong>, or edits on <strong>Data → Flight legs</strong>)."
          : "Use <strong>Manual Input</strong> (and Bulk CSV on desktop) until Supabase is configured."
      }</p>
      <p class="sheets-hint muted-small"><strong>Who is “personal”:</strong> ${basisEsc} — same as <strong>Maintenance → Business vs personal</strong>. One worksheet row = one trip in the counts below.</p>
      <div class="scope-dual-callout" role="note">
        <h3 class="scope-dual-title">Two date scopes (do not mix them)</h3>
        <dl class="scope-dual-list">
          <div class="scope-dual-item">
            <dt>Scope A — Calendar windows (first table)</dt>
            <dd><strong>This month</strong>, <strong>This quarter</strong>, <strong>Year to date</strong> (through ${escapeHtml(asOf)} on this device), and <strong>All time</strong>. These columns use the <strong>same calendar buckets</strong> as <strong>Direct operating costs</strong> (Honda + fuel rows on that tab). They do <em>not</em> follow Form 720’s date picker.</dd>
          </div>
          <div class="scope-dual-item">
            <dt>Scope B — Form 720 date range (second block)</dt>
            <dd>Only legs in <strong>${periodEsc}</strong> — the same range as the Form 720 quarter table, company metrics on <strong>Maintenance</strong>, and the IRS line detail. <button type="button" class="tab-jump-link" data-open-main-tab="f720">Open Form 720 tab</button> to change it. <button type="button" class="tab-jump-link" data-open-main-tab="invoice">UGC invoice</button> drafts reimbursement from this scope.</dd>
          </div>
        </dl>
      </div>
    </section>
    <section class="panel summary-cost-section">
      <h2>Calendar windows <span class="muted-small">(Scope A)</span></h2>
      <p class="sheets-hint muted-small">Personal trips only; dollar columns are the reimbursement piece of Utah Gas totals for that calendar slice.</p>
      ${scopeACalendarBucketsExplainHtml(asOf)}
      <div class="table-scroll">
        <table class="totals summary-cost-table ugc-personal-horizons">
          <thead>
            <tr>
              <th scope="col">Period</th>
              <th scope="col">Personal trips <span class="muted-small">(rows)</span></th>
              <th scope="col">Air excise <span class="muted-small">(26+27)</span></th>
              <th scope="col">2× fuel</th>
              <th scope="col">Total</th>
            </tr>
          </thead>
          <tbody>${horizonRows}</tbody>
        </table>
      </div>
    </section>
    <section class="panel ugc-f720-scope-block">
      <h2>Totals for Form 720 date range <span class="muted-small">(Scope B)</span></h2>
      <p class="sheets-hint company-period-banner"><strong>Active range:</strong> ${periodEsc} — same as <strong>Form 720 → Date range</strong> and <strong>Maintenance</strong> program rollups.</p>
      ${personalPaymentHints(rollup.personalBasis, f720S)}
      <details class="timeshare-f720-details">
        <summary>Why is 2× fuel or air excise sometimes $0?</summary>
        <p class="sheets-hint muted-small timeshare-f720-details-body">2× fuel needs <strong>gallons</strong> (from fuel lbs + Jet A lbs/gal on Tax rates, or entered gallons) <em>and</em> <strong>$/gal</strong> on the leg. Many older exports have lbs without price — those stay <strong>$0.00</strong> until you add $/gal on <strong>Manual Input</strong> or in the database. Air excise can be $0 on personal legs with no taxable passengers/segments or legs marked skip.</p>
      </details>
      <table class="totals company-mini">
        <tr><td>Personal — air excise (IRS 26+27)</td><td><strong>${formatMoney(f720S.personalAirExciseReimburseTotal)}</strong>${f720LegMeta}</td></tr>
        <tr><td>Personal — 2× fuel cost</td><td><strong>${formatMoney(f720S.personalFuelOwed2xTotal)}</strong>${f720LegMeta}</td></tr>
        <tr><td><strong>Total (personal → Utah Gas Corp)</strong></td><td><strong>${formatMoney(f720Total)}</strong>${f720LegMeta}</td></tr>
      </table>
    </section>
    <section class="panel panel-tight">
      <p class="sheets-hint muted-small">Worksheet estimate only — not a filed return. Confirm with your tax adviser and current Form 720 instructions.</p>
    </section>
  `;
}

function updateTexasRenditionPanel(
  root: HTMLElement,
  legs: FlightLeg[],
  rates: Rates,
  err: string | null
): void {
  const el = root.querySelector("#texas-worksheet-snapshot");
  if (!el) return;
  if (err) {
    el.innerHTML = `<section class="panel panel-tight"><p class="error" role="alert">${escapeHtml(err)}</p></section>`;
    return;
  }
  const rollup = readRollupCtx(root);
  const sel = readTexasPeriodSelection(root);
  const fl = filterLegsByPeriod(legs, sel);
  const { hourly, landing } = readHondaUsdRates(root);
  const s = summarizeCompanyHonda(fl, rates, hourly, landing, {
    isPersonalLeg: rollup.isPersonalLeg,
    texasLanding: rollup.texasLanding,
  });
  const periodEsc = escapeHtml(formatPeriodRangeLabel(sel));
  const totalDep = s.totalTakeoffs;
  const txDep = s.takeoffsTexas;
  const depPct =
    totalDep > 0 ? ((100 * txDep) / totalDep).toFixed(1) : null;
  const depPctCell = depPct !== null ? `${depPct}%` : "—";

  const totalLd = s.totalLandings;
  const txLd = s.totalTexasLandings;
  const ldPct =
    totalLd > 0 ? ((100 * txLd) / totalLd).toFixed(1) : null;
  const ldPctCell = ldPct !== null ? `${ldPct}%` : "—";

  el.innerHTML = `
    <section class="panel">
      <h2 class="tab-panel-heading">Texas departures</h2>
      <p class="sheets-hint muted-small texas-panel-scope"><strong>Date range</strong> (controls on this tab): ${periodEsc} · <strong>${fl.length}</strong> leg(s) in scope.</p>
      <p class="sheets-hint muted-small">For <strong>business aircraft</strong> allocation, Tax Code <strong>Section 21.055</strong> presumes Texas value using <strong>departures from Texas</strong> ÷ <strong>total departures</strong> (year before tax year). This table uses each leg’s <strong>takeoff</strong> count as “departures” (blank takeoffs → 1). Counts as departing Texas when <code>departure_state</code> is <strong>TX</strong> and/or <code>from</code> ICAO is Texas with <strong>Infer Texas… from airport code</strong> on (Form 720 tab) — same rules as <strong>Direct operating costs → Takeoffs departing TX</strong>.</p>
      <table class="totals company-mini texas-departures-summary">
        <tr><td>Total departures <span class="muted-small">(sum of leg takeoffs in range)</span></td><td><strong>${totalDep}</strong></td></tr>
        <tr><td>Departures from Texas</td><td><strong>${txDep}</strong></td></tr>
        <tr><td>Texas share of departures</td><td><strong>${depPctCell}</strong></td></tr>
      </table>
      <details class="panel-tight texas-landings-details">
        <summary class="sheets-hint muted-small">Texas landings (not the Section 21.055 fraction)</summary>
        <p class="sheets-hint muted-small">Statutory allocation uses <strong>departures</strong>, not arrivals. These landing counts are kept for cross-check only: Texas when <code>Texas arrival</code> and/or <code>to</code> ICAO is Texas (infer on). Same worksheet legs; if zeros, load or refresh flights.</p>
        <table class="totals company-mini texas-landings-summary">
          <tr><td>Total landings <span class="muted-small">(cycles)</span></td><td><strong>${totalLd}</strong></td></tr>
          <tr><td>Texas landings</td><td><strong>${txLd}</strong></td></tr>
          <tr><td>Texas share of landings</td><td><strong>${ldPctCell}</strong></td></tr>
        </table>
      </details>
    </section>
  `;
}

function updateMaintenanceRollups(
  root: HTMLElement,
  legs: FlightLeg[],
  rates: Rates,
  err: string | null
): void {
  const el = root.querySelector("#maintenance-period-rollups");
  if (!el) return;
  if (err) {
    el.innerHTML = `<p class="error" role="alert">${escapeHtml(err)}</p>`;
    return;
  }
  const rollup = readRollupCtx(root);
  const sel = readPeriodSelection(root);
  const fl = filterLegsByPeriod(legs, sel);
  const monthly = readHondaMonthlyUsd(root);
  const { hourly, landing } = readHondaUsdRates(root);
  const { months, flatTotal } = flatFeeMonthsInPeriod(legs, sel, monthly);
  const s = summarizeCompanyHonda(fl, rates, hourly, landing, {
    isPersonalLeg: rollup.isPersonalLeg,
    texasLanding: rollup.texasLanding,
  });
  const allIn = flatTotal + s.hondaVariableSubtotal;
  const periodLabel = escapeHtml(formatPeriodRangeLabel(sel));
  const flatCaption = escapeHtml(flatFeeMonthsCaption(sel, months));
  const cloud = hasSupabaseConfig();
  const rollupsNote = cloud
    ? `<p class="sheets-hint muted-small">Honda dollars below use worksheet legs (including Supabase <code>flight_legs</code> when loaded). <strong>Legs in scope</strong> counts rows whose date falls in the Form 720 range.</p>`
    : `<p class="sheets-hint muted-small">Honda dollars below use worksheet legs only. Add Supabase to sync <code>flight_legs</code> into the worksheet.</p>`;
  el.innerHTML = `
    ${rollupsNote}
    <div class="company-block">
      <h3 class="company-block-title">Honda program totals (selected period)</h3>
      <p class="sheets-hint"><strong>${periodLabel}</strong> · same controls as <strong>Form 720 → Date range</strong>. Legs in scope: <strong>${fl.length}</strong>.</p>
      <table class="totals company-mini">
        <tr><td>Flat fee total <span class="muted-small">(${flatCaption} × ${formatMoney(monthly)})</span></td><td>${formatMoney(flatTotal)}</td></tr>
        <tr><td>Total hours flown</td><td>${s.totalFlightHours.toFixed(2)}</td></tr>
        <tr><td>Hourly maintenance <span class="muted-small">(${formatMoney(hourly)} × hours)</span></td><td>${formatMoney(s.hondaHourlyCharge)}</td></tr>
        <tr><td>Landings (cycles)</td><td>${s.totalLandings}</td></tr>
        <tr><td>Landing / cycle maintenance <span class="muted-small">(${formatMoney(landing)} × landings)</span></td><td>${formatMoney(s.hondaLandingCharge)}</td></tr>
        <tr><td><strong>All-in Honda program total</strong> <span class="muted-small">(flat + hourly + landing/cycle)</span></td><td><strong>${formatMoney(allIn)}</strong></td></tr>
      </table>
    </div>
  `;
}

/** Non-zero IRS 26–27 pieces for the Federal per trip table (reconciles total vs pax columns). */
function federalPerTripTaxPartsHtml(r: TripTaxLine): string {
  const parts: string[] = [];
  if (r.percentageTax > 0) {
    parts.push(`7.5% charter: ${formatMoney(r.percentageTax)}`);
  }
  if (r.domesticSegmentTax > 0) {
    parts.push(
      `Dom segment: ${formatMoney(r.domesticSegmentTax)} <span class="muted-small">(${r.domesticSegmentsForTax} seg × ${r.effectiveDomesticSegmentPax} pax)</span>`
    );
  }
  if (r.internationalFacilityTax > 0) {
    parts.push(
      `Intl facility: ${formatMoney(r.internationalFacilityTax)} <span class="muted-small">(${r.internationalFacilityPax} × rate)</span>`
    );
  }
  if (r.alaskaHawaiiDepartureTax > 0) {
    parts.push(`AK/HI departure: ${formatMoney(r.alaskaHawaiiDepartureTax)}`);
  }
  return parts.length > 0 ? parts.join("<br/>") : "—";
}

function updateSummaryPeriodPanel(
  root: HTMLElement,
  legs: FlightLeg[],
  rates: Rates,
  err: string | null,
  duplicateRowsRemoved: number
): void {
  const el = root.querySelector("#summary-period-report");
  if (!el) return;
  if (err || legs.length === 0) {
    summaryScopedQuartersCache = null;
    summaryPersonalPct720Cache = null;
    summaryQuarterDetailCtx = null;
    if (err) {
      el.innerHTML = `<p class="error" role="alert">${escapeHtml(err)}</p>`;
      return;
    }
    el.innerHTML = "";
    return;
  }
  const rollup = readRollupCtx(root);
  const sel = readPeriodSelection(root);
  const flAll = filterLegsByPeriod(legs, sel);
  const fl = flAll.filter(rollup.isPersonalLeg);
  const aggOpts = { isPersonalLeg: rollup.isPersonalLeg };
  const trips = perTripFederalLines(fl, rates);
  const qMap = aggregateByQuarter(fl, rates, aggOpts);
  const pctBasis = readPersonalF720PctBasisFromForm(root);
  const personalPctByQ = personalTransportPercentageTaxByQuarter(fl, rates, {
    basis: pctBasis,
    isPersonalLeg: rollup.isPersonalLeg,
  });
  summaryPersonalPct720Cache = { basis: pctBasis, byQuarter: personalPctByQ };
  summaryQuarterDetailCtx = {
    scopedLegs: fl,
    rates,
    isPersonalLeg: rollup.isPersonalLeg,
  };
  const rollupOpts = {
    isPersonalLeg: rollup.isPersonalLeg,
    texasLanding: rollup.texasLanding,
  };
  const f720ScopeNote =
    flAll.length > 0 && fl.length < flAll.length
      ? `<p class="sheets-hint muted-small" role="note"><strong>Form 720</strong> on this tab uses <strong>${fl.length}</strong> personal-classified leg(s) in the date range (${flAll.length} total). <strong>Maintenance</strong>, <strong>Direct operating</strong>, and <strong>Timeshare</strong> program rollups still use every leg in range.</p>`
      : flAll.length > 0 && fl.length === 0
        ? `<p class="sheets-hint" role="note">No legs classified as <strong>personal</strong> in this date range — Form 720 quarters and federal-per-trip are empty. Company business legs stay in the worksheet for <strong>Maintenance</strong> and operating costs.</p>`
        : "";
  let scopeByQuarterHtml = "";
  for (const [, qt] of qMap) {
    const legsQ = filterLegsByPeriod(fl, {
      mode: "quarter",
      year: qt.year,
      quarter: qt.quarter as 1 | 2 | 3 | 4,
    });
    const legsAllInQ = filterLegsByPeriod(flAll, {
      mode: "quarter",
      year: qt.year,
      quarter: qt.quarter as 1 | 2 | 3 | 4,
    });
    const tsQ = timeshareRollup(legsQ, rates, rollupOpts);
    const toQ = totalTakeoffs(legsQ);
    const txDepPctQ = toQ > 0 ? (100 * tsQ.texasDepartureTotal) / toQ : null;
    scopeByQuarterHtml += `
    <div class="quarter-scope-block">
      <h3 class="quarter-title">${qt.year} Q${qt.quarter}</h3>
      <table class="totals">${scopeTotalsRowsHtml(tsQ, toQ, txDepPctQ, legsAllInQ.length)}</table>
    </div>`;
  }

  const dupNote =
    duplicateRowsRemoved > 0
      ? `<p class="sheets-hint muted-small" role="status">${duplicateRowsRemoved} duplicate row(s) omitted from totals (same date, route, tach, fuel, and flags).</p>`
      : "";

  let qHtml = "";
  for (const [, t] of qMap) {
    qHtml += `<tr><td>${t.year} Q${t.quarter}</td><td>${t.legCount}</td><td>${formatMoney(totalExciseAir(t))}</td></tr>`;
  }

  const qKeys = [...qMap.keys()];
  summaryScopedQuartersCache = qMap;
  const selectedKeys = readSummaryQuarterSelectionKeys(qKeys);
  const detailBody =
    qKeys.length === 0
      ? `<p class="sheets-hint">No legs in this date range.</p>`
      : quarterDetailCombinedHtml(
          qMap,
          selectedKeys,
          fl,
          rates,
          rollup.isPersonalLeg,
          summaryPersonalPct720Cache
        );
  const checkboxes =
    qKeys.length === 0
      ? ""
      : qKeys
          .map((k) => {
            const qt = qMap.get(k)!;
            const checked = selectedKeys.includes(k) ? " checked" : "";
            return `<label class="quarter-check-label"><input type="checkbox" name="summary-quarter-pick" value="${escapeHtml(k)}"${checked} /> <span>${qt.year} Q${qt.quarter} <span class="muted-small">(${qt.legCount} legs)</span></span></label>`;
          })
          .join("");

  const tripRows = trips
    .map(
      (r: TripTaxLine) =>
        `<tr><td>${escapeHtml(r.date)}</td><td>${escapeHtml(r.from)}–${escapeHtml(r.to)}</td><td>${r.passengerCount}</td><td>${r.effectiveDomesticSegmentPax}</td><td>${r.domesticSegmentsForTax}</td><td>${r.internationalFacilityPax}</td><td class="federal-per-trip-parts muted-small">${federalPerTripTaxPartsHtml(r)}</td><td>${formatMoney(r.federalAirExcise)}</td></tr>`
    )
    .join("");

  el.innerHTML = `
    <section class="panel">
      <h2>Scope</h2>
      ${f720ScopeNote}
      ${dupNote}
      <p class="sheets-hint muted-small">Each heading is a <strong>calendar quarter</strong> within <strong>Date range</strong>. Fuel and passenger sums apply to <strong>personal-classified</strong> legs only (same set as Form 720 below). Other legs in that quarter (e.g. business) still live in the worksheet — they are listed as <strong>excluded from Form 720 here</strong>, not as “zero business.”</p>
      ${
        qKeys.length === 0
          ? `<p class="sheets-hint">No legs in this date range.</p>`
          : scopeByQuarterHtml
      }
    </section>
    <section class="panel">
      <h2>Form 720 (scoped)</h2>
      <p class="sheets-hint muted-small">By calendar quarter for <strong>personal</strong> legs in <strong>Date range</strong> only. Totals use <code>aggregateByQuarter</code> / <code>legAirExciseBreakdown</code>: <strong>international facility</strong> does not stack with domestic segment; <strong>AK/HI → contiguous <code>K…</code></strong> stacks <strong>No. 26</strong> domestic segment with <strong>No. 27</strong> AK/HI departure on the same leg. Check one or more quarters for the IRS line breakdown (amounts sum when multiple are selected).</p>
      <table class="totals">
        <thead><tr><th>Quarter</th><th>Legs</th><th>Combined air excise</th></tr></thead>
        <tbody>${qHtml || "<tr><td colspan=\"3\">No legs in scope</td></tr>"}</tbody>
      </table>
      ${
        qKeys.length === 0
          ? ""
          : `<fieldset class="quarter-pick-fieldset" data-testid="summary-quarter-picks">
        <legend class="quarter-pick-legend">Quarter(s) — IRS line detail</legend>
        <div class="quarter-check-grid">${checkboxes}</div>
      </fieldset>
      <div id="summary-quarter-detail-body">${detailBody}</div>`
      }
    </section>
    <details class="panel federal-per-trip-details">
      <summary>Federal per trip <span class="muted-small">(${trips.length} legs in scope)</span></summary>
      <p class="sheets-hint muted-small">The <strong>total</strong> column is <strong>7.5%×Charter $</strong> (when set) <strong>plus</strong> passenger excise: <strong>international</strong> vs <strong>domestic</strong> is either/or; <strong>Alaska/Hawaii → <code>K…</code></strong> adds <strong>both</strong> domestic segment and AK/HI departure on that leg. <strong>Log pax</strong> is raw <code>passenger_count</code>. <strong>Dom pax</strong> / <strong>Intl pax</strong> feed those lines. If log pax and effective domestic segment pax differ, set <code>dom_pax</code> on the leg or fix <code>passenger_count</code>.</p>
      <table class="totals federal-per-trip-table">
        <thead><tr><th>Date</th><th>Leg</th><th title="passenger_count in log / CSV">Log pax</th><th title="Pax for IRS No. 26 domestic segment">Dom pax</th><th title="segments_domestic (minimum 1)">Segs</th><th title="intl_pax when US ↔ non-US">Intl pax</th><th>Tax parts</th><th>Total</th></tr></thead>
        <tbody>${tripRows || "<tr><td colspan=\"8\">None</td></tr>"}</tbody>
      </table>
    </details>
  `;
}

function flightDetailHtml(leg: FlightLeg, rates: Rates, rollup?: RollupCtx): string {
  const gal = resolvedFuelGallons(leg, rates.fuelLbsPerGallon);
  const fc = fuelCostUsd(leg, rates);
  const hrs = effectiveFlightHours(leg);
  const tach = leg.tachStart != null && leg.tachEnd != null ? leg.tachEnd - leg.tachStart : null;
  const fed = legAirExcise(leg, rates);
  const txLd = effectiveTexasLandings(leg, rollup?.texasLanding);
  const txDep = effectiveTexasDepartures(leg, rollup?.texasLanding);
  const rollupPersonal = rollup ? rollup.isPersonalLeg(leg) : leg.personalUse;
  const p2 = personalFuelOwed2x(leg, rates, rollupPersonal);
  const rows: [string, string][] = [
    ["Date", escapeHtml(leg.flightDate)],
    ["Aircraft", escapeHtml(leg.aircraftId)],
    ["From / To", `${escapeHtml(leg.fromIcao)} → ${escapeHtml(leg.toIcao)}`],
    ["Route / notes", escapeHtml(leg.routeNotes)],
    ["Tach start / end", `${leg.tachStart ?? "—"} / ${leg.tachEnd ?? "—"}`],
    ["Tach hours", tach !== null ? String(tach) : "—"],
    ["Flight hours (effective)", hrs !== null ? String(hrs) : "—"],
    ["Fuel lb", leg.fuelLbs != null ? String(leg.fuelLbs) : "—"],
    ["Fuel gal", gal !== null ? String(Math.round(gal * 100) / 100) : "—"],
    ["Fuel $/gal", leg.fuelPricePerGallon != null ? String(leg.fuelPricePerGallon) : "—"],
    ["Fuel cost", fc !== null ? formatMoney(fc) : "—"],
    ...(leg.crewList?.trim()
      ? ([["Crew", escapeHtml(leg.crewList.trim())]] as [string, string][])
      : []),
    ["Passengers (guests / non-crew)", escapeHtml(leg.passengerList ?? "")],
    ["Passenger count (log / CSV)", String(leg.passengerCount)],
    [
      "Domestic segment pax (excise)",
      String(effectiveDomesticSegmentPassengers(leg)),
    ],
    ["Domestic segment pax — how counted", escapeHtml(taxablePassengerListSummary(leg))],
    [
      "AK/HI dep pax (log / database)",
      String(Math.max(0, leg.alaskaHawaiiDomesticDeparturePassengers)),
    ],
    [
      "AK/HI dep pax (excise)",
      (() => {
        const stored = Math.max(0, leg.alaskaHawaiiDomesticDeparturePassengers);
        const eff = effectiveAlaskaHawaiiDeparturePassengers(leg);
        if (stored === 0 && eff > 0) {
          return `${eff} (inferred: departure airport is PA… or PH… US ICAO; matches IRS No. 26 AK/HI domestic departure when log left akhi blank)`;
        }
        return String(eff);
      })(),
    ],
    ["Personal (log / CSV)", leg.personalUse ? "yes" : "no"],
    ...(rollup
      ? [
          [
            "Personal (worksheet rollup)",
            resolvePersonalLeg(leg, rollup.personalBasis) ? "yes" : "no",
          ] as [string, string],
          ...(rollup.personalBasis === "notes_and_airports"
            ? ([
                [
                  "Rollup signals (notes + airports)",
                  escapeHtml(rollupClassificationSummary(classifyLegForRollup(leg))),
                ],
              ] as [string, string][])
            : []),
        ]
      : []),
    ["Personal 2× fuel → Utah Gas Corp (rollup)", formatMoney(p2)],
    ["Federal air excise (trip)", formatMoney(fed)],
    ["Takeoffs / landings", `${leg.takeoffs ?? 1} / ${leg.landings ?? 1}`],
    ["Texas arrival (log)", leg.texasArrival ? "yes" : "no"],
    ["Texas departures (takeoffs, this leg, rollup)", String(txDep)],
    ["Texas landings (cycles, this leg, rollup)", String(txLd)],
    ["Dep state", escapeHtml(leg.departureState ?? "")],
  ];
  return `<table class="totals">${rows.map(([a, b]) => `<tr><td>${a}</td><td>${b}</td></tr>`).join("")}</table>`;
}

function buildJson(
  legs: FlightLeg[],
  form720PersonalLegs: FlightLeg[],
  rates: Rates,
  quarters: Map<string, import("./lib/types").QuarterlyTotals>,
  quarterMonths: Map<string, MonthlyForm720Totals[]>,
  dataSource: "sheets" | "form",
  companyHonda:
    | {
        honda_usd_per_flight_hour: number;
        honda_usd_per_landing: number;
        summary: CompanyHondaSummary;
      }
    | null,
  hondaMaintenance: HondaMaintenanceSnapshot | null,
  personalTransport720: { basis: PersonalTransport720PctBasis; byQuarter: Map<string, number> }
): string {
  let form720PersonalLegsTallied = 0;
  const q: Record<string, unknown> = {};
  const pctLegOpts = {
    basis: personalTransport720.basis,
    isPersonalLeg: (_leg: FlightLeg) => true,
  };
  for (const [k, t] of quarters) {
    form720PersonalLegsTallied += t.legCount;
    const optPct =
      personalTransport720.basis === "off"
        ? "0"
        : String(personalTransport720.byQuarter.get(k) ?? 0);
    const moList = quarterMonths.get(k) ?? [];
    q[k] = {
      irs_26_percentage_tax: String(t.irs26PercentageTax),
      irs_26_domestic_segment_tax: String(t.irs26DomesticSegmentTax),
      irs_26_ak_hi_departure_tax: String(t.irs26AkHiDepartureTax),
      irs_26_total: String(irs26Total(t)),
      irs_27_international_facility_tax: String(t.irs27InternationalFacilityTax),
      total_air_excise: String(totalExciseAir(t)),
      personal_reimbursement_air_excise: String(t.personalReimbursementAirExcise),
      fuel_gallons_from_log: String(t.fuelGallonsFromLog),
      leg_count: t.legCount,
      optional_personal_transport_pct_tax_720: optPct,
      part_two_months: moList.map((mo, idx) => {
        const legsM = legsInCalendarMonth(
          form720PersonalLegs,
          mo.calendarYear,
          mo.calendarMonth
        );
        const optPctMo =
          personalTransport720.basis === "off"
            ? 0
            : personalTransportPercentageTaxTotal(legsM, rates, pctLegOpts);
        const combined = money(totalExciseAir(mo) + optPctMo);
        return {
          part_two_sequence: idx + 1,
          calendar_year: String(mo.calendarYear),
          calendar_month: String(mo.calendarMonth),
          irs_26_percentage_tax: String(mo.irs26PercentageTax),
          irs_26_domestic_segment_tax: String(mo.irs26DomesticSegmentTax),
          irs_26_ak_hi_departure_tax: String(mo.irs26AkHiDepartureTax),
          irs_26_total: String(irs26Total(mo)),
          irs_27_international_facility_tax: String(mo.irs27InternationalFacilityTax),
          total_air_excise: String(totalExciseAir(mo)),
          personal_reimbursement_air_excise: String(mo.personalReimbursementAirExcise),
          fuel_gallons_from_log: String(mo.fuelGallonsFromLog),
          leg_count: mo.legCount,
          optional_personal_transport_pct_tax_720: String(optPctMo),
          combined_air_excise_plus_optional_personal_pct: String(combined),
        };
      }),
    };
  }
  return JSON.stringify(
    {
      _export: {
        format: "flights-f720-worksheet",
        version: 2,
        exported_at: new Date().toISOString(),
        data_source: dataSource,
        form720_quarters_scope: "personal_classified_legs_only",
        form720_quarters_leg_count_sum: form720PersonalLegsTallied,
      },
      filer: { business_name: "Utah Gas Corp" },
      leg_count: legs.length,
      legs,
      rates: {
        domestic_segment_usd_per_passenger_per_segment: rates.domesticSegmentUsdPerPassengerPerSegment,
        international_facility_usd_per_person: rates.internationalFacilityUsdPerPerson,
        air_transportation_percentage: rates.airTransportationPercentage,
        alaska_hawaii_domestic_departure_usd_per_person:
          rates.alaskaHawaiiDomesticDepartureUsdPerPerson,
        fuel_lbs_per_gallon: rates.fuelLbsPerGallon,
      },
      personal_transport_720_pct_basis: personalTransport720.basis,
      quarters: q,
      ...(companyHonda
        ? {
            company_honda: {
              honda_usd_per_flight_hour: companyHonda.honda_usd_per_flight_hour,
              honda_usd_per_landing: companyHonda.honda_usd_per_landing,
              total_takeoffs: companyHonda.summary.totalTakeoffs,
              takeoffs_departing_texas: companyHonda.summary.takeoffsTexas,
              total_landings: companyHonda.summary.totalLandings,
              total_flight_hours: companyHonda.summary.totalFlightHours,
              hours_business: companyHonda.summary.hoursBusiness,
              hours_personal: companyHonda.summary.hoursPersonal,
              trips_business: companyHonda.summary.tripsBusiness,
              trips_personal: companyHonda.summary.tripsPersonal,
              pct_trips_business: companyHonda.summary.pctTripsBusiness,
              pct_hours_business: companyHonda.summary.pctHoursBusiness,
              honda_hourly_charge: companyHonda.summary.hondaHourlyCharge,
              honda_landing_charge: companyHonda.summary.hondaLandingCharge,
              honda_variable_subtotal: companyHonda.summary.hondaVariableSubtotal,
              personal_air_excise_reimburse_total:
                companyHonda.summary.personalAirExciseReimburseTotal,
            },
          }
        : {}),
      ...(hondaMaintenance
        ? {
            honda_maintenance: {
              monthly_program_usd: hondaMaintenance.monthly_program_usd,
              flight_hour_usd: hondaMaintenance.flight_hour_usd,
              landing_cycle_usd: hondaMaintenance.landing_cycle_usd,
              service_contract: hondaMaintenance.service_contract,
            },
          }
        : {}),
    },
    null,
    2
  );
}

function render(
  root: HTMLElement,
  legs: FlightLeg[],
  rates: Rates,
  err: string | null,
  dataSource: "sheets" | "form",
  meta?: { preDedupeLegCount?: number }
): void {
  const errorEl = root.querySelector("#error")!;
  const jsonEl = root.querySelector("#json-out")!;

  errorEl.textContent = err ?? "";
  errorEl.classList.toggle("hidden", !err);

  const rollup = readRollupCtx(root);
  const dupRemoved =
    meta?.preDedupeLegCount !== undefined
      ? Math.max(0, meta.preDedupeLegCount - legs.length)
      : 0;

  updateSummaryPeriodPanel(root, legs, rates, err, dupRemoved);
  updateSummaryCostOverviewPanels(root, legs, rates, err);
  updateMaintenanceRollups(root, legs, rates, err);

  if (err || legs.length === 0) {
    summaryScopedQuartersCache = null;
    summaryPersonalPct720Cache = null;
    const legsF720 = legs.filter(rollup.isPersonalLeg);
    const aggOpts = { isPersonalLeg: rollup.isPersonalLeg };
    const qEmpty = aggregateByQuarter(legsF720, rates, aggOpts);
    const qMonthsEmpty = aggregateExciseByQuarterMonths(legsF720, rates, aggOpts);
    const pctBasis = readPersonalF720PctBasisFromForm(root);
    const personal720 = {
      basis: pctBasis,
      byQuarter: personalTransportPercentageTaxByQuarter(legsF720, rates, {
        basis: pctBasis,
        isPersonalLeg: rollup.isPersonalLeg,
      }),
    };
    jsonEl.textContent = buildJson(
      legs,
      legsF720,
      rates,
      qEmpty,
      qMonthsEmpty,
      dataSource,
      null,
      readHondaMaintenanceSnapshotForExport(root),
      personal720
    );
    updateCompanyPanel(root, legs, rates, err);
    updateUtahGasPersonalPanel(root, legs, rates, err);
    updateUgcInvoicePanel(root, legs, rates, err);
    updateTexasRenditionPanel(root, legs, rates, err);
    updateF720RetrospectivePanel(root, legsF720, rates);
    updateComparisonPanel(root, legs, err, rates);
    return;
  }

  const legsF720 = legs.filter(rollup.isPersonalLeg);
  const aggOptsFull = { isPersonalLeg: rollup.isPersonalLeg };
  const quarters = aggregateByQuarter(legsF720, rates, aggOptsFull);
  const quarterMonths = aggregateExciseByQuarterMonths(legsF720, rates, aggOptsFull);

  const hondaR = readHondaUsdRates(root);
  const companyHonda = {
    honda_usd_per_flight_hour: hondaR.hourly,
    honda_usd_per_landing: hondaR.landing,
    summary: summarizeCompanyHonda(legs, rates, hondaR.hourly, hondaR.landing, {
      isPersonalLeg: rollup.isPersonalLeg,
      texasLanding: rollup.texasLanding,
    }),
  };
  const pctBasisFull = readPersonalF720PctBasisFromForm(root);
  const personal720Full = {
    basis: pctBasisFull,
    byQuarter: personalTransportPercentageTaxByQuarter(legsF720, rates, {
      basis: pctBasisFull,
      isPersonalLeg: rollup.isPersonalLeg,
    }),
  };
  jsonEl.textContent = buildJson(
    legs,
    legsF720,
    rates,
    quarters,
    quarterMonths,
    dataSource,
    companyHonda,
    readHondaMaintenanceSnapshotForExport(root),
    personal720Full
  );
  updateCompanyPanel(root, legs, rates, err);
  updateUtahGasPersonalPanel(root, legs, rates, err);
  updateUgcInvoicePanel(root, legs, rates, err);
  updateTexasRenditionPanel(root, legs, rates, err);
  updateF720RetrospectivePanel(root, legsF720, rates);
  updateComparisonPanel(root, legs, err, rates);
}

function renderLogin(app: HTMLDivElement): void {
  document.documentElement.classList.remove("ipad-bulk-off");
  app.innerHTML = `
    <div class="login-wrap panel">
      <p class="eyebrow">Utah Gas Corp · ${DEFAULT_AIRCRAFT_ID} · Form 720 &amp; time sharing agreement</p>
      <h1>Flight Log</h1>
      <p id="login-err" class="login-error" role="alert"></p>
      <form id="login-form">
        <label class="field">Email
          <input id="login-user" type="email" autocomplete="username" required />
        </label>
        <label class="field">Password
          <input id="login-pass" type="password" autocomplete="current-password" required />
        </label>
        <label class="field login-remember">
          <input id="login-remember" type="checkbox" checked />
          Remember me on this device (stay signed in after closing the browser)
        </label>
        <div class="actions" style="margin-top:1rem">
          <button type="submit">Sign in</button>
        </div>
      </form>
    </div>
  `;

  const form = app.querySelector<HTMLFormElement>("#login-form")!;
  const errEl = app.querySelector<HTMLParagraphElement>("#login-err")!;

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    errEl.textContent = "";
    const u = app.querySelector<HTMLInputElement>("#login-user")!.value;
    const p = app.querySelector<HTMLInputElement>("#login-pass")!.value;
    const remember = app.querySelector<HTMLInputElement>("#login-remember")?.checked ?? true;
    if (tryLogin(u, p, remember)) {
      mountWorksheet(app);
    } else {
      errEl.textContent = "Invalid email or password.";
    }
  });
}

function monthSelectOptionsHtml(selectedMonth: number): string {
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ] as const;
  return names
    .map((name, i) => {
      const v = i + 1;
      const sel = v === selectedMonth ? " selected" : "";
      return `<option value="${v}"${sel}>${name}</option>`;
    })
    .join("");
}

function mountWorksheet(app: HTMLDivElement): void {
  const ipadLayout = isIPadLike();
  document.documentElement.classList.toggle("ipad-bulk-off", ipadLayout);

  let legsCsv: FlightLeg[] = [];
  /** Supabase `flight_legs` snapshot for worksheet math; not rendered on Manual Input. */
  let worksheetCloudLegs: FlightLeg[] = [];
  let dataSource: "sheets" | "form" = "form";
  /** When manual parse throws mid-edit, keep showing the last good legs instead of wiping the sheet. */
  let lastOkLegsRaw: FlightLeg[] = [];
  const showCloud = hasSupabaseConfig();
  const tipsAddLegsLine = ipadLayout
    ? `<li><strong>Add legs</strong> on <strong>Manual Input</strong>; totals and this tab update automatically.</li>`
    : `<li><strong>Add legs</strong> on <strong>Manual Input</strong> or <strong>Bulk Input</strong>; totals and this tab update automatically.</li>`;
  const tipsBulkLine = ipadLayout
    ? `<li><strong>Bulk CSV</strong> — open this same URL on a <strong>Mac or PC</strong> in Safari or Chrome to upload a worksheet file (not shown on iPad).</li>`
    : `<li><strong>Bulk Input</strong> accepts a UTF-8 CSV from your log or worksheet (same fields as Manual Input).</li>`;
  const airportSuggestRef: { refresh: () => Promise<void> } = {
    refresh: async () => {},
  };

  app.innerHTML = `
    <div class="sign-out-wrap">
      <button type="button" id="sign-out">Sign out</button>
    </div>
    <header>
      <p class="eyebrow">Utah Gas Corp · ${DEFAULT_AIRCRAFT_ID} · Form 720 &amp; time sharing agreement</p>
      <h1>Flight Log</h1>
      <p class="dashboard-lead sheets-hint muted-small">Custom worksheet for Utah Gas Corp’s <strong>aircraft time sharing agreement</strong> on <strong>${DEFAULT_AIRCRAFT_ID}</strong> (FAR § 91.501): log flights, Form 720–style <strong>passenger air excise</strong>, and time-sharee <strong>reimbursement</strong> to the time sharor (Utah Gas) — modeled on agreement §2.3 (e.g. IRS 26/27 allocable to personal legs + 2× fuel). Not a filed return.</p>
      ${
        ipadLayout
          ? `<p class="ipad-bulk-hint muted-small" role="note">This iPad layout includes every tab except <strong>Bulk CSV</strong>. Add or edit legs with <strong>Manual Input</strong> or <strong>Data</strong> (Supabase). To upload a worksheet CSV, open <strong>this same URL</strong> on a <strong>Mac or PC</strong> in Safari or Chrome.</p>`
          : ""
      }
    </header>

    <nav class="panel main-nav" aria-label="Worksheet sections">
      <div class="tabs tabs-main" role="tablist">
        <button type="button" role="tab" id="tab-form" aria-selected="true" aria-controls="panel-form">Manual Input</button>
        ${
          ipadLayout
            ? ""
            : `<button type="button" role="tab" id="tab-sheets" aria-selected="false" aria-controls="panel-sheets">Bulk Input</button>`
        }
        <button type="button" role="tab" id="tab-data" aria-selected="false" aria-controls="panel-data">Data</button>
        <button type="button" role="tab" id="tab-f720" aria-selected="false" aria-controls="panel-f720">Form 720</button>
        <button type="button" role="tab" id="tab-f720-retro" aria-selected="false" aria-controls="panel-f720-retro" title="Worksheet passenger assumptions vs IRS indexed amounts by quarter; totals include charter % plus Dom/Intl/AK-HI">F720 retro</button>
        <button type="button" role="tab" id="tab-doc" aria-selected="false" aria-controls="panel-doc">Direct operating costs</button>
        <button type="button" role="tab" id="tab-ugc" aria-selected="false" aria-controls="panel-ugc" title="Utah Gas Corp aircraft time sharing agreement — personal reimbursement">Timeshare</button>
        <button type="button" role="tab" id="tab-invoice" aria-selected="false" aria-controls="panel-invoice" title="Draft reimbursement invoice to Utah Gas Corp (Scope B)">UGC invoice</button>
        <button type="button" role="tab" id="tab-agreements" aria-selected="false" aria-controls="panel-agreements">Agreements</button>
        <button type="button" role="tab" id="tab-pilot-costs" aria-selected="false" aria-controls="panel-pilot-costs">Pilot costs</button>
        <button type="button" role="tab" id="tab-comparison" aria-selected="false" aria-controls="panel-comparison">Comparison</button>
        <button type="button" role="tab" id="tab-texas" aria-selected="false" aria-controls="panel-texas">Texas Rendition Form</button>
        <button type="button" role="tab" id="tab-maintenance" aria-selected="false" aria-controls="panel-maintenance">Maintenance</button>
        <button type="button" role="tab" id="tab-contacts" aria-selected="false" aria-controls="panel-contacts">Contacts</button>
      </div>
    </nav>

    <details class="panel panel-tight dashboard-quick-map">
      <summary class="help-how-summary">How tabs fit together</summary>
      <ul class="policy-list sheets-hint help-how-list">
        <li><strong>Manual Input</strong> / <strong>Bulk Input</strong> / <strong>Data</strong> — flights in the worksheet (one row per leg).</li>
        <li><strong>Form 720</strong> — <strong>Date range</strong>, tax rates, and quarter federal air excise table.</li>
        <li><strong>F720 retro</strong> — legacy <strong>$4.80</strong>-era domestic / intl / AK-HI vs IRS indexed amounts by <strong>quarter</strong> (illustrative delta).</li>
        <li><strong>Direct operating costs</strong> — Honda program and fuel for <strong>calendar</strong> month / quarter / YTD (same calendar columns as <strong>Timeshare → Scope A</strong> reimbursement table, without Utah Gas dollars here).</li>
        <li><strong>Timeshare</strong> — Time-sharee reimbursement to Utah Gas under your <strong>aircraft time sharing agreement</strong> (§2.3-style): <strong>Scope A</strong> (calendar) and <strong>Scope B</strong> (Form 720 range).</li>
        <li><strong>UGC invoice</strong> — Draft <strong>Scope B</strong> reimbursement text (air excise + 2× fuel), copy / download, or open your mail app.</li>
        <li><strong>Maintenance</strong> — Honda charges and business vs personal for the <strong>Form 720</strong> range.</li>
        <li><strong>Texas Rendition</strong> — rendition context plus <strong>Texas departures</strong> (Section 21.055-style: takeoffs from Texas vs total takeoffs) with its <strong>own date range</strong> (defaults to Form 720 once at load).</li>
        <li><strong>Agreements</strong>, <strong>Pilot costs</strong>, <strong>Comparison</strong> — contract text, vendor pilot invoices vs legs, Q4 time-share columns plus <strong>2026 Q1</strong> reference vs worksheet.</li>
      </ul>
    </details>

    <div id="panel-f720" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-f720">
      <section class="panel">
        <h2>Date range</h2>
        <div class="period-bar">
          <label class="field">View
            <select id="period-mode">
              <option value="all">All time</option>
              <option value="year">Year</option>
              <option value="quarter">Quarter</option>
              <option value="month">Month</option>
              <option value="ytd">Year to date</option>
            </select>
          </label>
          <div class="period-group-year">
            <label class="field">Year
              <input type="number" id="period-year" min="2000" max="2100" step="1" value="2026" />
            </label>
          </div>
          <div class="period-group-quarter">
            <label class="field">Quarter
              <select id="period-quarter">
                <option value="1">Q1</option>
                <option value="2">Q2</option>
                <option value="3">Q3</option>
                <option value="4">Q4</option>
              </select>
            </label>
          </div>
          <div class="period-group-month">
            <label class="field">Month
              <select id="period-month">
                <option value="1">Jan</option><option value="2">Feb</option><option value="3">Mar</option>
                <option value="4">Apr</option><option value="5">May</option><option value="6">Jun</option>
                <option value="7">Jul</option><option value="8">Aug</option><option value="9">Sep</option>
                <option value="10">Oct</option><option value="11">Nov</option><option value="12">Dec</option>
              </select>
            </label>
          </div>
          <div class="period-apply-wrap">
            <button type="button" id="period-apply" class="secondary">Apply / refresh</button>
          </div>
        </div>
        <p class="sheets-hint muted-small period-range-hint">Quarter tables, company metrics on <strong>Maintenance</strong>, and <strong>Timeshare → Scope B</strong> (Form 720 reimbursement block) all follow this range. Changes usually apply immediately; on iPad and some browsers the year field may need <strong>Apply / refresh</strong> or tapping outside the field.</p>
        <p class="sheets-hint muted-small f720-cross-links">Time sharing agreement reimbursement (Scope B) for <em>this same range</em>: <button type="button" class="tab-jump-link" data-open-main-tab="ugc">Timeshare (Scope B)</button> · <button type="button" class="tab-jump-link" data-open-main-tab="invoice">UGC invoice</button>. Calendar <strong>month / quarter / YTD</strong> reimbursement is on <button type="button" class="tab-jump-link" data-open-main-tab="ugc">Timeshare (Scope A)</button>. <button type="button" class="tab-jump-link" data-open-main-tab="doc">Direct operating costs</button> is Honda + fuel only for those same calendar columns.</p>
        <p class="sheets-hint muted-small f720-cross-links">Legacy worksheet rates vs indexed IRS passenger amounts by quarter: <button type="button" class="tab-jump-link" data-open-main-tab="f720-retro">F720 retro</button>.</p>
      </section>
      <section class="panel">
        <h2>Tax rates</h2>
        <div class="rates-grid">
          <label class="field">Domestic segment
            <input id="r-dom" type="number" step="0.01" value="${defaultRates.domesticSegmentUsdPerPassengerPerSegment}" title="$/pax/segment" />
          </label>
          <label class="field">Intl facility
            <input id="r-intl" type="number" step="0.01" value="${defaultRates.internationalFacilityUsdPerPerson}" title="$/person" />
          </label>
          <label class="field">Tax rate
            <input id="r-pct" type="number" step="0.001" value="${defaultRates.airTransportationPercentage}" title="Decimal (e.g. 0.075 for 7.5%)" />
          </label>
          <label class="field">Alaska / Hawaii departure <span class="muted-small">($/person)</span>
            <input id="r-akhi" type="number" step="0.01" value="${defaultRates.alaskaHawaiiDomesticDepartureUsdPerPerson}" title="$/person on covered domestic departures (IRS No. 26)" />
          </label>
          <label class="field">Jet A lbs/gal
            <input id="r-fuel" type="number" step="0.1" value="${defaultRates.fuelLbsPerGallon}" />
          </label>
          <label class="field">Personal transport — optional % base (IRS No. 26)
            <select id="personal-f720-pct-basis">
              <option value="off">Off (charter $ only)</option>
              <option value="fuel">Tax rate × personal fuel cost</option>
              <option value="fuel2x" selected>Tax rate × personal 2× fuel cost</option>
            </select>
          </label>
        </div>
        <p class="sheets-hint muted-small">Form 720 totals, JSON <code>quarters</code>, and the <strong>F720 retrospective</strong> table use <strong>personal-classified legs only</strong>. Business legs stay in the log for maintenance, Honda program, and other operating views.</p>
        <p class="sheets-hint muted-small">The <strong>Tax rate</strong> (e.g. 7.5%) applies only to each leg’s <strong>Charter $</strong> / <code>charter_usd</code> — not to passenger counts, fuel, or ForeFlight “time share fuel” fields. Passenger-based IRS No. 26 shows on <strong>domestic segment</strong> (and intl / AK-HI) instead. <code>npm run convert:logbook</code> leaves <code>charter_usd</code> at 0 unless you add taxable fare amounts in the sheet or log.</p>
        <p class="sheets-hint muted-small">The <strong>Personal transport</strong> selector rolls <strong>7.5% ×</strong> personal legs’ fuel cost (or <strong>2×</strong> fuel cost when that option is selected) into the quarter table’s <strong>IRS No. 26 — percentage</strong> line along with tax on <strong>Charter $</strong>. JSON still lists charter percentage tax and optional personal transport tax separately. Use only if your filing position includes that base in the Form 720 percentage tax.</p>
      </section>
      <details class="help-how panel panel-tight">
        <summary class="help-how-summary">Tips</summary>
        <p class="sheets-hint">This worksheet estimates <strong>passenger air excise</strong> (Form 720–style) from your log. It is <strong>not</strong> a filed return — your tax pro still uses official IRS forms and instructions.</p>
        <ul class="policy-list help-how-list">
          ${tipsAddLegsLine}
          <li><strong>Each leg</strong> needs date, from, to, and passenger count at minimum. Add fuel and price per gallon when you care about fuel cost or personal reimbursement.</li>
          <li><strong>Personal / time-share</strong> drives the <strong>Timeshare</strong> tab and Utah Gas reimbursement lines (your <strong>aircraft time sharing agreement</strong> model) — route notes, passengers, airports, and the log <code>personal</code> / time share rules on CSV import.</li>
          ${tipsBulkLine}
          <li><strong>Aircraft</strong> in the app is always <strong>${DEFAULT_AIRCRAFT_ID}</strong>.</li>
          <li>With Supabase, legs are filled from the cloud on load. Edits on <strong>Data → Flight legs</strong> sync back into the worksheet automatically.</li>
        </ul>
      </details>
      <section class="panel">
        <h2 class="tab-panel-heading">Form 720 (federal air excise)</h2>
        <p class="panel-lead">Quarter tables and IRS line detail use the <strong>Date range</strong> and <strong>Tax rates</strong> above, on <strong>personal-classified</strong> legs only (same filter as Summary → Scope). Passenger rules match the Comparison tab: <strong>intl vs domestic</strong> is either/or; <strong>AK/HI → contiguous <code>K…</code></strong> uses <strong>both</strong> domestic segment (No. 26) and AK/HI departure (No. 27). For an IRS-template PDF (draft filer + IRS Nos. 26–27), open <strong>IRS Form 720 (PDF)</strong> below and click <strong>Download filled Form 720 (PDF)</strong>.</p>
      </section>
      <section class="panel panel-tight" id="form720-pdf-panel" aria-label="IRS Form 720 PDF export">
        <h3 class="tab-panel-heading">IRS Form 720 (PDF)</h3>
        <p class="sheets-hint muted-small">Draft fill of the official <a href="https://www.irs.gov/pub/irs-pdf/f720.pdf" rel="noopener noreferrer" target="_blank">IRS Form 720</a> (same file as <code>public/irs/f720.pdf</code>). Uses the same <strong>personal</strong> legs, <strong>checked quarter(s)</strong>, and <strong>per-leg passenger rules</strong> as the IRS line detail below (intl excludes domestic; AK/HI→<code>K…</code> stacks No. 26 + No. 27). Filer block matches <code>src/config/form720-filer.example.json</code> (quarter ending from the selection). <strong>Part I row order</strong> on page 1 is IRS No. <strong>26</strong> (persons), then <strong>28</strong> (property by air — left blank here; 6.25% cargo not modeled), then <strong>27</strong> (international + Alaska/Hawaii departure amounts). <strong>pdf-lib</strong> drops XFA metadata (console may warn). <strong>Claims</strong> (Part III line 4 / Schedule C) are for specific fuel and other credits — this tool does not fill them; leave blank if you have no claims. Confirm amounts with your preparer.</p>
        <p class="form720-pdf-actions">
          <button type="button" id="form720-download-pdf" class="secondary">Download filled Form 720 (PDF)</button>
        </p>
        <p id="form720-pdf-msg" class="sheets-hint muted-small" role="status" aria-live="polite"></p>
      </section>
      <div id="summary-period-report"></div>
    </div>

    <div id="panel-f720-retro" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-f720-retro">
      <div id="f720-retro-root" aria-live="polite"></div>
    </div>

    <div id="panel-doc" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-doc">
      <div id="summary-direct-op-panel"></div>
    </div>

    <div id="panel-ugc" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-ugc">
      <div id="ugc-personal-panel" aria-live="polite"></div>
    </div>

    <div id="panel-invoice" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-invoice">
      <section class="panel">
        <h2 class="tab-panel-heading">Utah Gas Corp — reimbursement invoice (draft)</h2>
        <p class="panel-lead">Build a plain-text invoice from <strong>Timeshare Scope B</strong> — the same personal-trip totals as <strong>Form 720 → Date range</strong> (allocable IRS 26+27 on personal legs and 2× fuel where priced).</p>
        <p class="sheets-hint muted-small">Change <strong>Date range</strong> on the <button type="button" class="tab-jump-link" data-open-main-tab="f720">Form 720</button> tab, then refresh here. <strong>Invoice date</strong> resets to today when you use <strong>Refresh from worksheet</strong>. <strong>Download JSON</strong> saves structured data. <strong>Send via email</strong> opens your mail app — nothing is sent server-side. Lease “Utah Gas must invoice you” vs. this reimbursement draft: <code>docs/UGC_REIMBURSEMENT_INVOICES.md</code>.</p>
        <div id="ugc-invoice-summary" aria-live="polite"></div>
        <div class="ugc-invoice-fields">
          <label class="field">From (time-sharee)
            <input type="text" id="ugc-invoice-remitter" autocomplete="name" />
          </label>
          <label class="field">Invoice / document #
            <input type="text" id="ugc-invoice-doc" autocomplete="off" placeholder="Blank → N426DH-TS-&lt;invoice date&gt;" />
          </label>
          <label class="field">Invoice date
            <input type="date" id="ugc-invoice-date" />
          </label>
          <label class="field">Bill to email
            <input type="email" id="ugc-invoice-to-email" autocomplete="email" placeholder="fd@fdunleavy.com" />
          </label>
        </div>
        <label class="field">Message body <span class="muted-small">(edit freely; use Refresh to replace from worksheet)</span>
          <textarea id="ugc-invoice-body" class="ugc-invoice-body" rows="20" spellcheck="true"></textarea>
        </label>
        <div class="ugc-invoice-actions">
          <button type="button" id="ugc-invoice-refresh" class="secondary">Refresh from worksheet</button>
          <button type="button" id="ugc-invoice-copy">Copy</button>
          <button type="button" id="ugc-invoice-download" class="secondary">Download .txt</button>
          <button type="button" id="ugc-invoice-download-json" class="secondary">Download JSON</button>
          <button type="button" id="ugc-invoice-mailto">Send via email…</button>
        </div>
        <p id="ugc-invoice-msg" class="sheets-hint muted-small" role="status" aria-live="polite"></p>
        ${
          showCloud
            ? `
        <section class="panel ugc-invoice-snapshots">
          <h3 class="tab-panel-heading">Saved drafts (Supabase)</h3>
          <p class="sheets-hint muted-small">Append-only copies of what you save from this tab — <strong>not</strong> automatic email. For the lease’s “Utah Gas must invoice within 10 days” clause (operator → time-sharee), see <code>docs/UGC_REIMBURSEMENT_INVOICES.md</code>.</p>
          <p class="ugc-invoice-cloud-actions">
            <button type="button" id="ugc-invoice-save-snapshot" class="secondary">Save current draft to cloud</button>
            <button type="button" id="ugc-invoice-refresh-history" class="secondary">Reload list</button>
          </p>
          <div id="ugc-invoice-history" class="ugc-invoice-history" role="region" aria-label="Saved invoice snapshots"></div>
        </section>`
            : `
        <p class="sheets-hint muted-small">With <strong>Supabase</strong> off, use <strong>Download .txt / JSON</strong> for records. With Supabase on, run migration <code>20260405180000_ugc_reimbursement_invoice_snapshots.sql</code> then <strong>Save current draft to cloud</strong> appears here. See <code>docs/UGC_REIMBURSEMENT_INVOICES.md</code>.</p>`
        }
      </section>
    </div>

    <div id="panel-agreements" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-agreements">
      <h2 class="tab-panel-heading">Agreements</h2>
      ${agreementsPanelInnerHtml()}
    </div>

    <div id="panel-pilot-costs" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-pilot-costs">
      <h2 class="tab-panel-heading">Pilot &amp; crew costs</h2>
      <p class="sheets-hint muted-small">Link <strong>vendor pilot/crew invoices</strong> to the flights they cover. Uses the same worksheet legs as Form 720 (Bulk, cloud, and Manual). Saved records live in <strong>Supabase</strong> when configured (<code>pilot_cost_invoices</code> / <code>pilot_cost_invoice_legs</code>).</p>
      <section class="panel pilot-costs-invoice-section">
        <h3 class="maint-subheading">Vendor invoice → flights covered</h3>
        <p class="sheets-hint muted-small">Match a <strong>pilot / crew vendor invoice</strong> to worksheet legs. Pick the <strong>service period</strong> (usually matches the bill), <strong>Load legs from worksheet</strong>, uncheck any rows the invoice does not cover, then <strong>Save</strong>. Legs are labeled by <strong>date and route</strong> (no separate flight names). Rows with a cloud <code>id</code> link to <code>flight_legs</code>; others still save with date/route snapshots.</p>
        <p id="pilot-inv-msg" class="sheets-hint muted-small" role="status" aria-live="polite"></p>
        <div class="pilot-inv-fields">
          <label class="field">Invoice title
            <input id="pilot-inv-title" type="text" maxlength="200" placeholder="e.g. CAP invoice — March 2026" autocomplete="off" />
          </label>
          <label class="field">Amount invoiced (USD)
            <input id="pilot-inv-amount" type="number" step="0.01" min="0" placeholder="0.00" autocomplete="off" />
          </label>
          <label class="field">Period start
            <input id="pilot-inv-period-start" type="date" />
          </label>
          <label class="field">Period end
            <input id="pilot-inv-period-end" type="date" />
          </label>
        </div>
        <label class="field" style="margin-top:0.65rem">Vendor / memo
          <textarea id="pilot-inv-vendor-note" rows="2" placeholder="Invoice #, vendor name, or internal note"></textarea>
        </label>
        <div class="pilot-inv-actions">
          <button type="button" id="pilot-inv-load-legs" class="secondary">Load legs from worksheet</button>
          <button type="button" id="pilot-inv-select-all" class="secondary">Select all shown</button>
          <button type="button" id="pilot-inv-select-none" class="secondary">Clear selection</button>
          <button type="button" id="pilot-inv-save">Save</button>
        </div>
        <div id="pilot-inv-leg-list" class="pilot-inv-leg-scroll" role="group" aria-label="Flights in period"></div>
        <h4 class="pilot-inv-saved-heading">Saved pilot invoices</h4>
        <div class="table-scroll">
          <table class="totals" id="pilot-inv-saved-table">
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Amount</th>
                <th scope="col">Period</th>
                <th scope="col">Legs</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody id="pilot-inv-saved-tbody"></tbody>
          </table>
        </div>
      </section>
    </div>

    <div id="panel-comparison" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-comparison">
      <h2 class="tab-panel-heading">Q4 2025 time share — email excerpt vs built-in benchmarks</h2>
      <p class="sheets-hint muted-small"><strong>N426DH</strong> — Q4 2025 <code>[Time share]</code>: column <strong>1</strong> = email-rate path ($4.80-era passenger lines + Form 720 fuel / charter %); column <strong>2</strong> = same <strong>$4.80 / $21.10 / $11.70</strong> passenger lines via merge; column <strong>3</strong> = <strong>$5.30 / $23.40 / $11.70</strong> worksheet-default passenger lines. Details load below.</p>
      <div id="comparison-panel-root" aria-live="polite"></div>
    </div>

    <div id="panel-texas" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-texas">
      <section class="panel">
        <h2 class="tab-panel-heading">Texas rendition (property tax)</h2>
        <p class="panel-lead">Texas counties levy tax on <strong>tangible business personal property</strong> used to produce income. Owners file a <strong>rendition</strong> with the chief appraiser in the taxing county (typically due <strong>April 15</strong>, with extensions available under the Tax Code). This app does <strong>not</strong> file renditions — use official forms and your appraisal district. For <strong>interstate business aircraft</strong>, allocation often turns on <strong>departures from Texas</strong> vs <strong>all departures</strong> (Tax Code Section 21.055) — not landings in Texas.</p>
        <ul class="policy-list sheets-hint">
          <li><strong>Form 50-144</strong> — <em>Business Personal Property Rendition</em> (common starting point for aircraft and other business equipment). Published by the Texas Comptroller; filed with the county appraisal district.</li>
          <li><strong>Form 50-159</strong> — <em>Aircraft Rendition of Taxable Value</em> — used when reporting aircraft to the CAD (requirements vary by situation).</li>
          <li><strong>Allocation / interstate use</strong> — separate comptroller allocation forms can apply when aircraft operate in multiple jurisdictions (e.g. Tax Code sections 21.05 and 21.055); confirm with your CAD and counsel.</li>
        </ul>
        <p class="sheets-hint muted-small">Penalties may apply for late or false renditions. Verify deadlines, forms, and aircraft-specific rules on <a href="https://comptroller.texas.gov/taxes/property-tax/forms/" rel="noopener noreferrer" target="_blank">comptroller.texas.gov — Property tax forms</a> and your county appraisal district site.</p>
      </section>
      <section class="panel panel-tight texas-landing-scope">
        <h3 class="maint-subheading">Departure counts — date range</h3>
        <p class="sheets-hint muted-small">Scoped <strong>only</strong> to the worksheet snapshot below (independent of Form 720 unless you use <strong>Match Form 720</strong>).</p>
        <div class="period-bar texas-period-bar">
          <label class="field">View
            <select id="texas-period-mode" aria-label="Texas departure counts period mode">
              <option value="all">All time</option>
              <option value="year">Year</option>
              <option value="quarter">Quarter</option>
              <option value="month">Month</option>
              <option value="ytd">Year to date</option>
            </select>
          </label>
          <div class="period-group-year texas-period-group-year">
            <label class="field">Year
              <input type="number" id="texas-period-year" min="2000" max="2100" step="1" value="2026" aria-label="Texas counts year" />
            </label>
          </div>
          <div class="period-group-quarter texas-period-group-quarter hidden">
            <label class="field">Quarter
              <select id="texas-period-quarter" aria-label="Texas counts quarter">
                <option value="1">Q1</option>
                <option value="2">Q2</option>
                <option value="3">Q3</option>
                <option value="4">Q4</option>
              </select>
            </label>
          </div>
          <div class="period-group-month texas-period-group-month hidden">
            <label class="field">Month
              <select id="texas-period-month" aria-label="Texas counts month">
                <option value="1">Jan</option><option value="2">Feb</option><option value="3">Mar</option>
                <option value="4">Apr</option><option value="5">May</option><option value="6">Jun</option>
                <option value="7">Jul</option><option value="8">Aug</option><option value="9">Sep</option>
                <option value="10">Oct</option><option value="11">Nov</option><option value="12">Dec</option>
              </select>
            </label>
          </div>
          <div class="period-apply-wrap">
            <button type="button" id="texas-period-apply" class="secondary">Apply</button>
          </div>
          <div class="period-apply-wrap">
            <button type="button" id="texas-period-match-f720" class="secondary" title="Copy Form 720 Date range settings">Match Form 720</button>
          </div>
        </div>
      </section>
      <div id="texas-worksheet-snapshot" aria-live="polite"></div>
    </div>

    <div id="panel-form" class="tab-panel" role="tabpanel" aria-labelledby="tab-form">
      <section class="panel">
        <h2 class="tab-panel-heading">Manual Input</h2>
        <p id="manual-form-lead" class="form-hint">Add <strong>new</strong> legs here (one card per flight). Totals update across tabs when you leave a field or change rates.${
          showCloud
            ? ` Saved history stays on <strong>Data → Flight legs</strong>; Form 720 and other tabs still include those rows when Supabase is on.`
            : ""
        } For <strong>quarter air excise</strong>, set <strong>Date range</strong> on <button type="button" class="tab-jump-link" data-open-main-tab="f720">Form 720</button> (not the calendar tables on Timeshare).</p>
        ${
          ipadLayout
            ? `<p class="form-hint muted-small">On iPad, enter legs here or use <strong>Data → Flight legs</strong> with Supabase. CSV bulk upload: use a Mac or PC.</p>`
            : ""
        }
        <div id="form-legs"></div>
        <button type="button" id="add-leg">Add flight</button>
        ${
          showCloud
            ? `<div class="cloud-row" style="margin-top:1rem">
          <button type="button" id="legs-load-cloud" class="secondary">Refresh from Supabase</button>
          <button type="button" id="legs-save-cloud" class="secondary">Save manual legs to Supabase</button>
          <p id="legs-cloud-msg" class="cloud-msg" role="status"></p>
        </div>`
            : ""
        }
      </section>
    </div>

    ${
      ipadLayout
        ? ""
        : `<div id="panel-sheets" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-sheets">
      <section class="panel">
        <h2 class="tab-panel-heading">Bulk Input</h2>
        <p class="sheets-hint sheets-steps-intro">Upload bulk records from a UTF-8 CSV. Replaces the previous upload in this session only.</p>
        <div class="dropzone">
          <input type="file" id="file" data-testid="csv-input" accept=".csv,text/csv" />
          <label class="dropzone-label" for="file">
            <strong>Upload CSV</strong><br />
            <span class="dropzone-sub">Choose file or drag and drop</span>
          </label>
        </div>
        <p id="csv-load-msg" class="cloud-msg" role="status" aria-live="polite"></p>
        ${
          showCloud
            ? `<p class="sheets-hint"><button type="button" id="csv-import-cloud" class="secondary">Import CSV rows to Supabase</button> <span id="csv-cloud-msg" class="cloud-msg" role="status"></span></p>`
            : ""
        }
      </section>
    </div>`
    }

    <div id="panel-maintenance" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-maintenance">
      <section class="panel">
        <h2 class="tab-panel-heading">Maintenance · ${DEFAULT_AIRCRAFT_ID}</h2>
        <p class="panel-lead">Honda program assumptions, service contract reference (audit context), and charges from <strong>flight log</strong> legs for the selected period.</p>

        <div class="maint-preset-row">
          <span class="maint-preset-label">Quick period</span>
          <button type="button" class="secondary maint-preset-btn" id="maint-preset-month">This month</button>
          <button type="button" class="secondary maint-preset-btn" id="maint-preset-quarter">This quarter</button>
          <button type="button" class="secondary maint-preset-btn" id="maint-preset-ytd">Year to date</button>
          <button type="button" class="secondary maint-preset-btn" id="maint-preset-all">All time</button>
        </div>
        <p id="maint-preset-feedback" class="sheets-hint muted-small maint-preset-feedback" role="status" aria-live="polite" aria-atomic="true"></p>
        <div class="period-bar maint-summary-scope-row">
          <label class="field">Summary — year
            <input type="number" id="maint-summary-year" min="2000" max="2100" step="1" value="${new Date().getFullYear()}" aria-label="Maintenance summary year" />
          </label>
          <label class="field">Summary — month
            <select id="maint-summary-month" aria-label="Maintenance summary month">
              ${monthSelectOptionsHtml(new Date().getMonth() + 1)}
            </select>
          </label>
        </div>
        <p class="sheets-hint maint-hint"><strong>Quick period</strong> sets <strong>Form 720 → Date range</strong> and updates <strong>Summary — year / month</strong> to match (quarter preset uses the first month of the current quarter). Change year or month anytime to review another month’s Honda totals in the table below without switching tabs.</p>

        <details class="panel panel-tight maint-contract-details" open>
          <summary class="maint-contract-summary">Service / contract reference</summary>
          <p class="sheets-hint">Stored in this browser for context. Does not replace hours and landings from the flight log.</p>
          <div class="maint-contract-grid">
            <label class="field">Vendor name
              <input type="text" id="honda-c-vendor" autocomplete="organization" />
            </label>
            <label class="field">Program / service name
              <input type="text" id="honda-c-program" autocomplete="off" />
            </label>
            <label class="field">Contract number
              <input type="text" id="honda-c-contract-no" autocomplete="off" />
            </label>
            <label class="field">Purchase order <span class="muted-small">(optional)</span>
              <input type="text" id="honda-c-po" autocomplete="off" />
            </label>
            <label class="field">Contract valid from
              <input type="date" id="honda-c-valid-from" />
            </label>
            <label class="field">Contract valid to
              <input type="date" id="honda-c-valid-to" />
            </label>
            <label class="field">Billing period start
              <input type="date" id="honda-c-bill-start" />
            </label>
            <label class="field">Billing period end
              <input type="date" id="honda-c-bill-end" />
            </label>
            <label class="field">Customer number <span class="muted-small">(optional)</span>
              <input type="text" id="honda-c-customer" autocomplete="off" />
            </label>
            <label class="field">Document date <span class="muted-small">(optional)</span>
              <input type="date" id="honda-c-doc-date" />
            </label>
            <label class="field">Currency
              <input type="text" id="honda-c-currency" maxlength="8" />
            </label>
            <label class="field">Document / invoice number
              <input type="text" id="honda-c-doc-num" autocomplete="off" />
            </label>
            <label class="field">Sold to / Bill to
              <input type="text" id="honda-c-sold-to" autocomplete="off" />
            </label>
            <label class="field">Email
              <input type="email" id="honda-c-email" autocomplete="email" />
            </label>
            <label class="field">Phone
              <input type="text" id="honda-c-phone" autocomplete="tel" />
            </label>
            <label class="field maint-field-span">Notes <span class="muted-small">(optional)</span>
              <textarea id="honda-c-notes" rows="2" placeholder="Optional reference notes"></textarea>
            </label>
          </div>
        </details>

        <h3 class="maint-subheading">Program rates (editable)</h3>
        <div class="honda-rates-row">
          <label class="field">Flat fee ($ / month)
            <input type="number" id="honda-usd-monthly" min="0" step="1" value="${DEFAULT_HONDA_USD_MONTHLY_PROGRAM}" />
          </label>
          <label class="field">Flight charge ($ / hour flown)
            <input type="number" id="honda-usd-hour" min="0" step="1" value="305" />
          </label>
          <label class="field">Landing / cycle rate ($)
            <input type="number" id="honda-usd-landing" min="0" step="1" value="260" />
          </label>
        </div>
        <div id="summary-maintenance-overview-panel"></div>
        <div id="maintenance-period-rollups"></div>
        <div id="company-metrics" class="company-metrics" aria-live="polite"></div>
      </section>
    </div>

    <div id="panel-data" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-data">
      ${
        showCloud
          ? `
      <section class="panel">
        <h2 class="tab-panel-heading">Flight legs (Supabase)</h2>
        <p class="sheets-hint"><button type="button" id="flights-refresh" class="secondary">Refresh</button> <span id="flights-msg" role="status"></span></p>
        <div class="table-scroll"><table class="flights-table" id="flights-table">
          <thead><tr><th>Date</th><th>From</th><th>To</th><th title="Sum of excise headcounts: domestic segment + international facility + Alaska/Hawaii departure (the same travelers may appear in more than one bucket when multiple lines apply — matches Form 720 math, including PA…/PH… departure inferred from ICAO when akhi is 0)">Pax</th><th>Actions</th></tr></thead>
          <tbody id="flights-tbody"></tbody>
        </table></div>
        <div id="flight-detail" class="flight-detail hidden"></div>
      </section>
      `
          : `
      <section class="panel">
        <h2 class="tab-panel-heading">Stored data</h2>
        <p class="sheets-hint"><strong>This app build cannot talk to Supabase</strong> — not because your project is empty, but because <code>VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_ANON_KEY</code> were <strong>not present when this site was built</strong>. Data you already imported in the Supabase dashboard is still there; this browser tab simply has no API keys embedded to load it.</p>
        <p class="sheets-hint"><strong>Fix:</strong> add both variables where the site is built (Vercel / Netlify env, or <code>web/.env</code> for local), then <strong>redeploy</strong> or restart <code>npm run dev</code>. Keys are <strong>not</strong> read from your laptop at runtime when you open a hosted URL. Full checklist: <code>docs/SUPABASE.md</code>.</p>
        <ul class="sheets-hint policy-list">
          <li><strong>File location:</strong> <code>web/.env</code> <em>or</em> <code>.env</code> at the <strong>repository root</strong> (this project merges both; restart dev after edits).</li>
          <li><strong>Exact names:</strong> <code>VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_ANON_KEY</code> (the <code>VITE_</code> prefix is required).</li>
          <li><strong>Local:</strong> after editing <code>web/.env</code>, stop the dev server and run <code>cd web &amp;&amp; npm run dev</code> again.</li>
          <li><strong>Hosted site (iPad, etc.):</strong> set both variables in your host (e.g. Vercel → Environment Variables) and <strong>redeploy</strong>; your laptop’s <code>.env</code> does not affect the deployed bundle.</li>
        </ul>
        <p class="sheets-hint">${escapeHtml(supabaseEnvDebug())}</p>
        ${
          bundleBuildCommit()
            ? `<p class="sheets-hint muted-small">Bundle Git commit: <code>${escapeHtml(bundleBuildCommit())}</code> — in Vercel → <strong>Deployments</strong>, open the deployment for <em>this</em> URL and confirm the commit matches.</p>`
            : ""
        }
        <p class="sheets-hint muted-small" role="note"><strong>If the Vercel build log shows Supabase keys merged</strong> but this page still says missing, you are usually on a <strong>different Vercel project</strong> (same GitHub repo linked more than once — env vars are per project), or the browser is serving a <strong>cached</strong> old <code>.js</code> file. Try incognito / hard refresh, or compare the commit above to the deployment you expect. See <code>docs/VERCEL.md</code>.</p>
        <p class="sheets-hint muted-small">${
          ipadLayout
            ? "Manual Input still works without Supabase."
            : "Manual Input and Bulk Input still work without Supabase."
        }</p>
      </section>
      `
      }
    </div>

    <div id="panel-contacts" class="tab-panel hidden" role="tabpanel" aria-labelledby="tab-contacts">
      <section class="panel">
        <h2 class="tab-panel-heading">Contacts</h2>
        <p class="sheets-hint muted-small contacts-tab-lead"><strong>Optional</strong> and <strong>last on purpose</strong> — name hints for <strong>Manual Input → Passengers</strong> when Supabase is on. Most days you use Form 720 and Data.</p>

        ${
          showCloud
            ? `<div class="contacts-block contacts-roster-block">
          <h3 class="contacts-block-title">Roster <span class="muted-small">(<code>public.contacts</code>)</span></h3>
          <p class="sheets-hint muted-small">Data comes straight from Supabase. Empty search lists up to <strong>150</strong> names A–Z; type to filter by name.</p>
          <div class="contacts-search-row">
            <label class="field contacts-search-field">Search by name
              <input type="search" id="contacts-search" placeholder="Type to filter…" autocomplete="off" enterkeyhint="search" />
            </label>
            <button type="button" id="contacts-roster-refresh" class="secondary">Refresh</button>
          </div>
          <p id="contacts-roster-msg" class="cloud-msg contacts-roster-msg" role="status"></p>
          <div class="table-scroll">
            <table class="totals contacts-browse-table" aria-label="Contacts from Supabase">
              <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Roles</th></tr></thead>
              <tbody id="contacts-roster-tbody"></tbody>
            </table>
          </div>
        </div>`
            : ""
        }

        <div class="contacts-block">
          <h3 class="contacts-block-title">1. Do you need this?</h3>
          <ul class="policy-list sheets-hint contacts-block-list">
            <li><strong>Already on each leg:</strong> <strong>crew</strong> and <strong>passengers</strong> as text (same rows as <strong>Data → Flight legs</strong>).</li>
            <li><strong>What <code>contacts</code> adds:</strong> passenger <strong>autocomplete</strong> on Manual Input when Supabase is configured.</li>
            <li><strong>What it does not replace:</strong> per-leg passenger lists or crew fields.</li>
          </ul>
        </div>

        <div class="contacts-block">
          <h3 class="contacts-block-title">2. Build <code>contacts.json</code></h3>
          <ul class="policy-list sheets-hint contacts-block-list">
            <li>In <code>web/</code>: <code>npm run convert:logbook</code> → <code>logbook_historical_data/contacts.json</code> (ForeFlight export).</li>
            <li>On a leg, separate multiple passengers with <code>;</code>.</li>
          </ul>
        </div>

        <div class="contacts-block contacts-placeholder-block" aria-label="Example contacts.json shape">
          <h3 class="contacts-block-title">3. Placeholder — file shape (example)</h3>
          <p class="sheets-hint muted-small">Dashed box = example only. File is a <strong>JSON array</strong> of objects. <strong>Extra fields</strong> from the converter are OK; import uses <code>name</code> (required), <code>email</code>, <code>phone</code>, and <code>rolesSeen</code>.</p>
          <pre class="contacts-json-placeholder">[
  {
    "name": "Alex Morgan",
    "email": "alex@example.com",
    "phone": "",
    "rolesSeen": ["Passenger", "PIC"],
    "legCount": 14
  },
  {
    "name": "Jamie Chen",
    "email": "",
    "phone": "555-0100",
    "rolesSeen": ["Passenger"],
    "legCount": 3
  }
]</pre>
        </div>

        <div class="contacts-block">
          <h3 class="contacts-block-title">4. Import</h3>
          ${
            showCloud
              ? `<ul class="policy-list sheets-hint contacts-block-list">
            <li>Each run <strong>inserts new rows</strong>. Same name twice → two rows unless you clean up in Supabase.</li>
          </ul>
          <input type="file" id="contacts-import-file" accept="application/json,.json" class="hidden" aria-hidden="true" />
          <div class="cloud-row contacts-import-row">
            <button type="button" id="contacts-import-pick" class="secondary">Choose contacts.json</button>
            <button type="button" id="contacts-import-run" class="secondary">Import to Supabase</button>
          </div>
          <p id="contacts-import-msg" class="cloud-msg contacts-import-status" role="status"></p>`
              : `<ul class="policy-list sheets-hint contacts-block-list">
            <li>This build has no Supabase keys. Set <code>VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_ANON_KEY</code> (see <code>docs/SUPABASE.md</code>) to import here.</li>
            <li>Or from a machine with <code>web/.env</code>: <code>npm run import:contacts</code> (CLI).</li>
          </ul>`
          }
        </div>
      </section>
    </div>

    <section class="panel actions-panel">
      <p id="error" class="error hidden" role="alert"></p>
    </section>

    <pre id="json-out" class="json-out hidden" aria-hidden="true" aria-label="Internal worksheet payload (Supabase)"></pre>

    <p class="disclaimer">
      Worksheet only. Not a filed return.
      <a href="https://www.irs.gov/instructions/i720" target="_blank" rel="noopener">Form 720 instructions</a> for filing rules.
    </p>
  `;

  applyHondaServiceContractToForm(app, loadHondaServiceContractFromStorage());
  hydratePersonalF720PctSelect(app);

  app.querySelector("#sign-out")!.addEventListener("click", () => {
    clearAuth();
    renderLogin(app);
  });

  type MainTab =
    | "form"
    | "sheets"
    | "data"
    | "contacts"
    | "f720"
    | "f720-retro"
    | "doc"
    | "ugc"
    | "invoice"
    | "agreements"
    | "pilot-costs"
    | "comparison"
    | "texas"
    | "maintenance";
  const MAIN_TABS: MainTab[] = ipadLayout
    ? [
        "form",
        "data",
        "f720",
        "f720-retro",
        "doc",
        "ugc",
        "invoice",
        "agreements",
        "pilot-costs",
        "comparison",
        "texas",
        "maintenance",
        "contacts",
      ]
    : [
        "form",
        "sheets",
        "data",
        "f720",
        "f720-retro",
        "doc",
        "ugc",
        "invoice",
        "agreements",
        "pilot-costs",
        "comparison",
        "texas",
        "maintenance",
        "contacts",
      ];

  const tabForm = app.querySelector<HTMLButtonElement>("#tab-form")!;
  const tabSheets = app.querySelector<HTMLButtonElement>("#tab-sheets");
  const tabData = app.querySelector<HTMLButtonElement>("#tab-data")!;
  const tabContacts = app.querySelector<HTMLButtonElement>("#tab-contacts")!;
  const tabF720 = app.querySelector<HTMLButtonElement>("#tab-f720")!;
  const tabF720Retro = app.querySelector<HTMLButtonElement>("#tab-f720-retro")!;
  const tabDoc = app.querySelector<HTMLButtonElement>("#tab-doc")!;
  const tabUgc = app.querySelector<HTMLButtonElement>("#tab-ugc")!;
  const tabInvoice = app.querySelector<HTMLButtonElement>("#tab-invoice")!;
  const tabAgreements = app.querySelector<HTMLButtonElement>("#tab-agreements")!;
  const tabPilotCosts = app.querySelector<HTMLButtonElement>("#tab-pilot-costs")!;
  const tabComparison = app.querySelector<HTMLButtonElement>("#tab-comparison")!;
  const tabTexas = app.querySelector<HTMLButtonElement>("#tab-texas")!;
  const tabMaintenance = app.querySelector<HTMLButtonElement>("#tab-maintenance")!;
  const panelSheets = app.querySelector<HTMLDivElement>("#panel-sheets");
  const formLegsRoot = app.querySelector<HTMLDivElement>("#form-legs")!;
  const fileInput = app.querySelector<HTMLInputElement>("#file");
  const addLegBtn = app.querySelector<HTMLButtonElement>("#add-leg")!;

  function resetManualEntryToOneBlankRow(): void {
    const lbs = readRatesFromForm(app).fuelLbsPerGallon;
    formLegsRoot.innerHTML = formLegRowHtml();
    refreshAllLegRowChrome(formLegsRoot, lbs);
  }

  function syncWorksheetLegsFromFlightArray(legs: FlightLeg[]): void {
    worksheetCloudLegs = legs;
    resetManualEntryToOneBlankRow();
  }

  function manualFormHasUnsavedOrPartialEntry(): boolean {
    const lbs = readRatesFromForm(app).fuelLbsPerGallon;
    try {
      return readLegsFromManualForm(formLegsRoot, lbs).length > 0;
    } catch {
      return true;
    }
  }

  /**
   * All legs that should drive totals (Form 720, Timeshare, Maintenance, etc.).
   * Bulk CSV (`legsCsv`), cloud worksheet, and Manual Input are merged and deduped so
   * switching tabs does not drop a loaded CSV. On the Bulk CSV tab we skip reading
   * Manual Input so a half-filled row there cannot block totals while you review CSV.
   */
  function currentLegs(): FlightLeg[] {
    const lbs = readRatesFromForm(app).fuelLbsPerGallon;
    const fromFile = [...legsCsv];
    const fromCloud = showCloud ? [...worksheetCloudLegs] : [];

    if (dataSource === "sheets") {
      return dedupeFlightLegs([...fromFile, ...fromCloud]);
    }

    const manual = readLegsFromManualForm(formLegsRoot, lbs);
    return dedupeFlightLegs([...fromFile, ...fromCloud, ...manual]);
  }

  const run = () => {
    const rates = readRatesFromForm(app);
    let legsRaw: FlightLeg[] = [];
    let err: string | null = null;
    try {
      legsRaw = currentLegs();
    } catch (e) {
      err = (e as Error).message;
    }
    if (err) {
      const fallback = dedupeFlightLegs(lastOkLegsRaw);
      const meta =
        lastOkLegsRaw.length > 0
          ? { preDedupeLegCount: lastOkLegsRaw.length }
          : undefined;
      render(app, fallback, rates, err, dataSource, meta);
      return;
    }
    lastOkLegsRaw = legsRaw;
    const legs = dedupeFlightLegs(legsRaw);
    const meta = { preDedupeLegCount: legsRaw.length };
    try {
      render(app, legs, rates, null, dataSource, meta);
    } catch (e) {
      render(app, legs, rates, (e as Error).message, dataSource, meta);
    }
  };

  formLegsRoot.innerHTML = formLegRowHtml();
  refreshAllLegRowChrome(formLegsRoot, readRatesFromForm(app).fuelLbsPerGallon);

  copyForm720PeriodToTexasPanel(app);
  syncTexasPeriodBarVisibility(app);

  requestAnimationFrame(() => {
    formLegsRoot.querySelector<HTMLInputElement>('[data-f="date"]')?.focus();
  });

  const fuelRateInput = app.querySelector<HTMLInputElement>("#r-fuel")!;

  formLegsRoot.addEventListener("input", (e) => {
    const t = e.target as HTMLElement;
    const row = t.closest("[data-leg-row]");
    if (!(row instanceof HTMLElement)) return;
    if (t.matches('[data-f="fuel_lbs"]')) {
      updateFuelGalVisibility(row);
      updateFuelGallonsForRow(row, readRatesFromForm(app).fuelLbsPerGallon);
    }
    if (dataSource === "form") run();
  });

  formLegsRoot.addEventListener("change", (e) => {
    const t = e.target as HTMLElement;
    const row = t.closest("[data-leg-row]");
    if (!(row instanceof HTMLElement)) return;
    if (t.matches('[data-f="to"]')) maybeAutoTexasArrival(row);
    if (dataSource === "form") run();
  });

  fuelRateInput.addEventListener("input", () => {
    syncAllFuelGallonsFromLbs(
      formLegsRoot,
      readRatesFromForm(app).fuelLbsPerGallon
    );
    run();
  });

  let contactsSearchDebounce: number | undefined;
  async function refreshContactsRoster(): Promise<void> {
    if (!showCloud) return;
    const tbody = app.querySelector("#contacts-roster-tbody");
    const msg = app.querySelector<HTMLParagraphElement>("#contacts-roster-msg");
    const input = app.querySelector<HTMLInputElement>("#contacts-search");
    if (!tbody) return;
    const q = (input?.value ?? "").trim();
    if (msg) msg.textContent = q ? "Searching…" : "Loading…";
    const { data, error } = q
      ? await searchContacts(q, 100)
      : await listContactsAlphabetical(150);
    if (error) {
      if (msg) msg.textContent = error;
      tbody.innerHTML = "";
      return;
    }
    const rows = data ?? [];
    if (msg) {
      msg.textContent =
        rows.length === 0
          ? q
            ? "No matching names."
            : "No rows in contacts yet — import JSON below (or run npm run import:contacts)."
          : q
            ? `${rows.length} match(es).`
            : `Showing ${rows.length} row(s), A–Z (max 150). Type to narrow.`;
    }
    tbody.innerHTML = rows
      .map(
        (r) =>
          `<tr><td>${escapeHtml(r.name)}</td><td>${escapeHtml(r.email)}</td><td>${escapeHtml(r.phone)}</td><td>${escapeHtml(r.roles)}</td></tr>`
      )
      .join("");
  }

  function scheduleContactsSearch(): void {
    window.clearTimeout(contactsSearchDebounce);
    contactsSearchDebounce = window.setTimeout(() => {
      void refreshContactsRoster();
    }, 280);
  }

  function activateMainTab(tab: MainTab): void {
    for (const k of MAIN_TABS) {
      app.querySelector(`#tab-${k}`)?.setAttribute("aria-selected", String(k === tab));
      app.querySelector(`#panel-${k}`)?.classList.toggle("hidden", k !== tab);
    }
    if (tab === "maintenance" || tab === "doc") {
      let legs: FlightLeg[] = [];
      let err: string | null = null;
      try {
        legs = currentLegs();
      } catch (e) {
        err = (e as Error).message;
      }
      const rates = readRatesFromForm(app);
      updateSummaryCostOverviewPanels(app, legs, rates, err);
      if (tab === "maintenance") {
        updateCompanyPanel(app, legs, rates, err);
        updateMaintenanceRollups(app, legs, rates, err);
      }
    }
    if (tab === "ugc") {
      let legs: FlightLeg[] = [];
      let err: string | null = null;
      try {
        legs = currentLegs();
      } catch (e) {
        err = (e as Error).message;
      }
      const rates = readRatesFromForm(app);
      updateUtahGasPersonalPanel(app, legs, rates, err);
    }
    if (tab === "invoice") {
      let legs: FlightLeg[] = [];
      let err: string | null = null;
      try {
        legs = currentLegs();
      } catch (e) {
        err = (e as Error).message;
      }
      const rates = readRatesFromForm(app);
      updateUgcInvoicePanel(app, legs, rates, err);
      if (showCloud) void refreshUgcInvoiceHistoryList();
    }
    if (tab === "pilot-costs") {
      if (showCloud) void refreshPilotCostSavedInvoices(app);
    }
    if (tab === "texas") {
      let legs: FlightLeg[] = [];
      let err: string | null = null;
      try {
        legs = currentLegs();
      } catch (e) {
        err = (e as Error).message;
      }
      const rates = readRatesFromForm(app);
      updateTexasRenditionPanel(app, legs, rates, err);
    }
    if (tab === "data" && showCloud) {
      void refreshFlightsTable();
    }
    if (tab === "contacts" && showCloud) {
      void refreshContactsRoster();
    }
  }

  function selectLegsTab(which: "sheets" | "form"): void {
    if (ipadLayout && which === "sheets") {
      dataSource = "form";
      activateMainTab("form");
      run();
      return;
    }
    dataSource = which;
    activateMainTab(which === "sheets" ? "sheets" : "form");
    run();
  }

  /**
   * Refresh in-memory worksheet from `flight_legs`. Manual Input stays a blank entry row;
   * cloud rows drive totals together with any legs typed on Manual (until refresh clears the form).
   */
  async function pullFlightLegsFromCloudIntoWorksheet(opts?: {
    focusManualTab?: boolean;
    confirmIfUnsavedManual?: boolean;
  }): Promise<{ ok: true; count: number } | { ok: false; error: string }> {
    const focusManual = opts?.focusManualTab !== false;
    const confirmUnsaved = opts?.confirmIfUnsavedManual !== false;
    if (confirmUnsaved && manualFormHasUnsavedOrPartialEntry()) {
      const ok = window.confirm(
        "Refresh from Supabase will clear Manual Input and reload worksheet totals from the cloud. Unsaved or incomplete rows there will be lost. Continue?"
      );
      if (!ok) return { ok: false, error: "Cancelled." };
    }
    const { data, error } = await listFlightLegs();
    if (error) return { ok: false, error };
    const legs = data ?? [];
    legsCsv = [];
    syncWorksheetLegsFromFlightArray(legs);
    if (focusManual) {
      selectLegsTab("form");
    } else {
      dataSource = "form";
      run();
    }
    void airportSuggestRef.refresh();
    return { ok: true, count: legs.length };
  }

  tabSheets?.addEventListener("click", () => selectLegsTab("sheets"));
  tabForm.addEventListener("click", () => selectLegsTab("form"));
  tabData.addEventListener("click", () => activateMainTab("data"));
  tabContacts.addEventListener("click", () => activateMainTab("contacts"));
  tabF720.addEventListener("click", () => {
    activateMainTab("f720");
    run();
  });
  tabF720Retro.addEventListener("click", () => {
    activateMainTab("f720-retro");
    run();
  });
  tabDoc.addEventListener("click", () => {
    activateMainTab("doc");
    run();
  });
  tabUgc.addEventListener("click", () => {
    activateMainTab("ugc");
    run();
  });
  tabInvoice.addEventListener("click", () => {
    activateMainTab("invoice");
    run();
  });
  tabAgreements.addEventListener("click", () => activateMainTab("agreements"));
  tabPilotCosts.addEventListener("click", () => activateMainTab("pilot-costs"));
  tabComparison.addEventListener("click", () => {
    activateMainTab("comparison");
    run();
  });
  tabTexas.addEventListener("click", () => activateMainTab("texas"));

  app.addEventListener("click", (e) => {
    const jump = (e.target as HTMLElement).closest("[data-open-main-tab]");
    if (!(jump instanceof HTMLElement)) return;
    const dest = jump.getAttribute("data-open-main-tab");
    if (!dest || !(MAIN_TABS as readonly string[]).includes(dest)) return;
    e.preventDefault();
    const tab = dest as MainTab;
    activateMainTab(tab);
    if (
      tab === "f720" ||
      tab === "f720-retro" ||
      tab === "doc" ||
      tab === "ugc" ||
      tab === "invoice" ||
      tab === "comparison"
    )
      run();
  });
  tabMaintenance.addEventListener("click", () => activateMainTab("maintenance"));

  function persistUgcInvoiceField(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  }

  async function refreshUgcInvoiceHistoryList(): Promise<void> {
    const el = app.querySelector("#ugc-invoice-history");
    if (!el || !showCloud) return;
    el.innerHTML = '<p class="muted-small">Loading…</p>';
    const { data, error } = await listUgcInvoiceSnapshots(40);
    if (error) {
      el.innerHTML = `<p class="error" role="alert">${escapeHtml(error)}</p>`;
      return;
    }
    if (!data || data.length === 0) {
      el.innerHTML =
        '<p class="muted-small">No saved drafts yet. Use <strong>Save current draft to cloud</strong> after refreshing from the worksheet.</p>';
      return;
    }
    const rows = data
      .map((r) => {
        const p = r.payload_json as {
          line_items_usd?: { total_due_to_operator?: number };
        };
        const totalN = p?.line_items_usd?.total_due_to_operator ?? 0;
        const period = r.scope_b_period_label;
        const periodShort =
          period.length > 72
            ? `${escapeHtml(period.slice(0, 72))}…`
            : escapeHtml(period);
        const saved = String(r.created_at).replace("T", " ").slice(0, 19);
        return `<tr><td>${escapeHtml(saved)}</td><td>${escapeHtml(r.document_number)}</td><td>${escapeHtml(r.invoice_date)}</td><td>${periodShort}</td><td class="ugc-snap-total">${formatMoney(totalN)}</td></tr>`;
      })
      .join("");
    el.innerHTML = `<div class="table-scroll"><table class="totals ugc-invoice-history-table"><thead><tr><th>Saved (UTC)</th><th>Document #</th><th>Invoice date</th><th>Scope B period</th><th>Total</th></tr></thead><tbody>${rows}</tbody></table></div>`;
  }

  app.querySelector("#ugc-invoice-doc")?.addEventListener("change", () => {
    const el = app.querySelector<HTMLInputElement>("#ugc-invoice-doc");
    if (el) persistUgcInvoiceField(UGC_INVOICE_DOC_KEY, el.value);
  });
  app.querySelector("#ugc-invoice-to-email")?.addEventListener("change", () => {
    const el = app.querySelector<HTMLInputElement>("#ugc-invoice-to-email");
    if (el) persistUgcInvoiceField(UGC_INVOICE_EMAIL_KEY, el.value);
  });
  app.querySelector("#ugc-invoice-remitter")?.addEventListener("change", () => {
    const el = app.querySelector<HTMLInputElement>("#ugc-invoice-remitter");
    if (el) persistUgcInvoiceField(UGC_INVOICE_REMITTER_KEY, el.value);
  });

  app.querySelector("#ugc-invoice-refresh")?.addEventListener("click", () => {
    const ta = app.querySelector<HTMLTextAreaElement>("#ugc-invoice-body");
    const dt = app.querySelector<HTMLInputElement>("#ugc-invoice-date");
    if (ta) ta.dataset.userEdited = "";
    if (dt) {
      dt.value = toIsoDateLocal(new Date());
      dt.dataset.userTouched = "";
    }
    run();
  });
  app.querySelector("#ugc-invoice-body")?.addEventListener("input", () => {
    const ta = app.querySelector<HTMLTextAreaElement>("#ugc-invoice-body");
    if (ta) ta.dataset.userEdited = "1";
  });
  app.querySelector("#ugc-invoice-date")?.addEventListener("change", () => {
    const dt = app.querySelector<HTMLInputElement>("#ugc-invoice-date");
    if (dt) dt.dataset.userTouched = "1";
  });
  app.querySelector("#ugc-invoice-copy")?.addEventListener("click", async () => {
    const ta = app.querySelector<HTMLTextAreaElement>("#ugc-invoice-body");
    const msg = app.querySelector<HTMLParagraphElement>("#ugc-invoice-msg");
    if (!ta) return;
    try {
      await navigator.clipboard.writeText(ta.value);
      if (msg) msg.textContent = "Copied to clipboard.";
    } catch {
      if (msg) msg.textContent = "Copy failed — select the text and copy manually.";
    }
  });
  app.querySelector("#ugc-invoice-download")?.addEventListener("click", () => {
    const ta = app.querySelector<HTMLTextAreaElement>("#ugc-invoice-body");
    if (!ta) return;
    let legs: FlightLeg[] = [];
    let rates = defaultRates;
    try {
      legs = currentLegs();
      rates = readRatesFromForm(app);
    } catch {
      /* use defaults for slug only */
    }
    const draftIn = buildUgcInvoiceDraftInputFromWorksheet(app, legs, rates);
    const doc = effectiveInvoiceDocumentNumber(
      draftIn.aircraftId,
      draftIn.issuedDateIso,
      draftIn.invoiceDocumentNumber
    );
    const blob = new Blob([ta.value], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ugc-reimbursement-${doc.replace(/[^\w.-]+/g, "-")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  });
  app.querySelector("#ugc-invoice-download-json")?.addEventListener("click", () => {
    let legs: FlightLeg[] = [];
    let rates = defaultRates;
    try {
      legs = currentLegs();
      rates = readRatesFromForm(app);
    } catch (e) {
      const msg = app.querySelector<HTMLParagraphElement>("#ugc-invoice-msg");
      if (msg) msg.textContent = (e as Error).message;
      return;
    }
    const draftIn = buildUgcInvoiceDraftInputFromWorksheet(app, legs, rates);
    const doc = effectiveInvoiceDocumentNumber(
      draftIn.aircraftId,
      draftIn.issuedDateIso,
      draftIn.invoiceDocumentNumber
    );
    const json = buildUgcInvoiceDataJson(draftIn);
    const blob = new Blob([json], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ugc-reimbursement-${doc.replace(/[^\w.-]+/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
    const msg = app.querySelector<HTMLParagraphElement>("#ugc-invoice-msg");
    if (msg) msg.textContent = "Downloaded invoice data JSON.";
  });
  app.querySelector("#ugc-invoice-mailto")?.addEventListener("click", () => {
    const ta = app.querySelector<HTMLTextAreaElement>("#ugc-invoice-body");
    const em = app.querySelector<HTMLInputElement>("#ugc-invoice-to-email")?.value?.trim();
    const msg = app.querySelector<HTMLParagraphElement>("#ugc-invoice-msg");
    if (!ta) return;
    if (!em) {
      if (msg) msg.textContent = "Add a Bill to email first.";
      return;
    }
    let legs: FlightLeg[] = [];
    let rates = defaultRates;
    try {
      legs = currentLegs();
      rates = readRatesFromForm(app);
    } catch {
      /* subject still useful */
    }
    const draftIn = buildUgcInvoiceDraftInputFromWorksheet(app, legs, rates);
    const sub = buildUgcInvoiceSubject(draftIn);
    const body = ta.value;
    const href = `mailto:${encodeURIComponent(em)}?subject=${encodeURIComponent(sub)}&body=${encodeURIComponent(body)}`;
    if (href.length > 2000) {
      if (msg) {
        msg.textContent =
          "Draft is too long for mailto — use Copy or Download, then paste or attach in your mail app.";
      }
      return;
    }
    if (msg) msg.textContent = "";
    window.location.href = href;
  });

  app.querySelector("#ugc-invoice-save-snapshot")?.addEventListener("click", async () => {
    const msg = app.querySelector<HTMLParagraphElement>("#ugc-invoice-msg");
    const ta = app.querySelector<HTMLTextAreaElement>("#ugc-invoice-body");
    if (!showCloud) return;
    let legs: FlightLeg[] = [];
    let rates = defaultRates;
    try {
      legs = currentLegs();
      rates = readRatesFromForm(app);
    } catch (e) {
      if (msg) msg.textContent = (e as Error).message;
      return;
    }
    const draftIn = buildUgcInvoiceDraftInputFromWorksheet(app, legs, rates);
    const payload = buildUgcInvoiceDataRecord(draftIn);
    const docEff = effectiveInvoiceDocumentNumber(
      draftIn.aircraftId,
      draftIn.issuedDateIso,
      draftIn.invoiceDocumentNumber
    );
    const toEmail =
      app.querySelector<HTMLInputElement>("#ugc-invoice-to-email")?.value?.trim() ??
      null;
    const remitter =
      app.querySelector<HTMLInputElement>("#ugc-invoice-remitter")?.value?.trim() ??
      null;
    const { error } = await insertUgcInvoiceSnapshot({
      invoice_date: draftIn.issuedDateIso,
      document_number: docEff,
      scope_b_period_label: draftIn.periodLabel,
      bill_to_email: toEmail,
      remitter_line: remitter,
      generated_at: payload.generated_at,
      payload_json: payload,
      plain_text: ta?.value ?? buildUgcInvoicePlainText(draftIn),
    });
    if (msg) {
      msg.textContent = error
        ? error
        : "Saved draft to Supabase. Reload list or open Table Editor → ugc_reimbursement_invoice_snapshots.";
    }
    if (!error) void refreshUgcInvoiceHistoryList();
  });
  app.querySelector("#ugc-invoice-refresh-history")?.addEventListener("click", () => {
    void refreshUgcInvoiceHistoryList();
  });

  const AGREEMENT_SUB_KEYS = ["overview", "timeshare", "purchase", "worksheet"] as const;
  type AgreementsSubKey = (typeof AGREEMENT_SUB_KEYS)[number];

  function activateAgreementsSub(sub: AgreementsSubKey): void {
    const wrap = app.querySelector("#panel-agreements");
    if (!wrap) return;
    for (const k of AGREEMENT_SUB_KEYS) {
      const btn = wrap.querySelector(`[data-agreements-sub="${k}"]`);
      const pane = wrap.querySelector(`#agreements-pane-${k}`);
      const on = k === sub;
      btn?.setAttribute("aria-selected", String(on));
      pane?.classList.toggle("hidden", !on);
    }
  }

  activateAgreementsSub("overview");

  app.querySelector("#panel-agreements")?.addEventListener("click", (e) => {
    const t = (e.target as HTMLElement).closest("[data-agreements-sub]");
    if (!(t instanceof HTMLButtonElement)) return;
    const raw = t.getAttribute("data-agreements-sub");
    if (
      raw === "overview" ||
      raw === "timeshare" ||
      raw === "purchase" ||
      raw === "worksheet"
    ) {
      activateAgreementsSub(raw);
    }
  });

  wirePilotCostInvoicePanel(app, { showCloud, currentLegs });

  app.querySelector("#honda-usd-hour")?.addEventListener("input", () => run());
  app.querySelector("#honda-usd-landing")?.addEventListener("input", () => run());
  app.querySelector("#honda-usd-monthly")?.addEventListener("input", () => run());

  app.querySelector<HTMLInputElement>("#maint-summary-year")?.addEventListener("change", () =>
    run()
  );
  app.querySelector<HTMLInputElement>("#maint-summary-year")?.addEventListener("input", () => run());
  app.querySelector<HTMLInputElement>("#maint-summary-year")?.addEventListener("blur", () => {
    const el = app.querySelector<HTMLInputElement>("#maint-summary-year");
    if (el) {
      const y = parseInt(el.value, 10);
      const nowY = new Date().getFullYear();
      if (!Number.isFinite(y) || y < 2000 || y > 2100) el.value = String(nowY);
    }
    run();
  });
  for (const ev of ["change", "input", "blur"] as const) {
    app.querySelector("#maint-summary-month")?.addEventListener(ev, () => run());
  }

  const applyPeriodAndRun = (): void => {
    syncPeriodBarVisibility(app);
    run();
  };
  app.querySelector("#period-mode")?.addEventListener("change", applyPeriodAndRun);
  app.querySelector("#period-mode")?.addEventListener("input", applyPeriodAndRun);
  for (const id of ["#period-year", "#period-quarter", "#period-month"]) {
    const el = app.querySelector(id);
    el?.addEventListener("change", applyPeriodAndRun);
    el?.addEventListener("input", applyPeriodAndRun);
    /** iOS / iPad: number spinners and year field often omit `input` until blur. */
    el?.addEventListener("blur", applyPeriodAndRun);
  }
  app.querySelector("#period-apply")?.addEventListener("click", applyPeriodAndRun);
  syncPeriodBarVisibility(app);

  const applyTexasPeriodAndRun = (): void => {
    syncTexasPeriodBarVisibility(app);
    run();
  };
  app.querySelector("#texas-period-mode")?.addEventListener("change", applyTexasPeriodAndRun);
  app.querySelector("#texas-period-mode")?.addEventListener("input", applyTexasPeriodAndRun);
  for (const id of ["#texas-period-year", "#texas-period-quarter", "#texas-period-month"]) {
    const el = app.querySelector(id);
    el?.addEventListener("change", applyTexasPeriodAndRun);
    el?.addEventListener("input", applyTexasPeriodAndRun);
    el?.addEventListener("blur", applyTexasPeriodAndRun);
  }
  app.querySelector("#texas-period-apply")?.addEventListener("click", applyTexasPeriodAndRun);
  app.querySelector("#texas-period-match-f720")?.addEventListener("click", () => {
    copyForm720PeriodToTexasPanel(app);
    syncTexasPeriodBarVisibility(app);
    run();
  });

  function wireFlightDetailEditorFuel(editor: HTMLElement): void {
    editor.addEventListener("input", (ev) => {
      const t = ev.target as HTMLElement;
      const row = t.closest("[data-leg-row]");
      if (!(row instanceof HTMLElement)) return;
      if (t.matches('[data-f="fuel_lbs"]')) {
        updateFuelGalVisibility(row);
        updateFuelGallonsForRow(row, readRatesFromForm(app).fuelLbsPerGallon);
      }
    });
    editor.addEventListener("change", (ev) => {
      const t = ev.target as HTMLElement;
      const row = t.closest("[data-leg-row]");
      if (!(row instanceof HTMLElement)) return;
      if (t.matches('[data-f="to"]')) maybeAutoTexasArrival(row);
    });
  }

  function renderFlightDetailReadOnly(
    detail: HTMLElement,
    id: string,
    leg: FlightLeg,
    rates: Rates
  ): void {
    detail.innerHTML = `
      <h3 class="flight-detail-title">Flight ${escapeHtml(id.slice(0, 8))}…</h3>
      ${flightDetailHtml(leg, rates, readRollupCtx(app))}
      <p class="flight-detail-actions">
        <button type="button" class="secondary" id="flight-detail-edit">Edit</button>
        <button type="button" class="secondary" id="flight-detail-delete">Delete</button>
        <button type="button" class="secondary" id="flight-detail-close">Close</button>
      </p>
      <p id="flight-detail-msg" class="cloud-msg" role="status"></p>
    `;
    detail.querySelector("#flight-detail-close")?.addEventListener("click", () => {
      detail.classList.add("hidden");
      detail.innerHTML = "";
    });
    detail.querySelector("#flight-detail-edit")?.addEventListener("click", () => {
      renderFlightDetailEdit(detail, id, leg);
    });
    detail.querySelector("#flight-detail-delete")?.addEventListener("click", async () => {
      if (!window.confirm("Delete this flight row?")) return;
      const { error: delErr } = await deleteFlightLeg(id);
      if (delErr) {
        const m = detail.querySelector("#flight-detail-msg");
        if (m) m.textContent = delErr;
        return;
      }
      detail.classList.add("hidden");
      detail.innerHTML = "";
      void refreshFlightsTable();
      void pullFlightLegsFromCloudIntoWorksheet({
        focusManualTab: false,
        confirmIfUnsavedManual: false,
      });
    });
  }

  function renderFlightDetailEdit(detail: HTMLElement, id: string, leg: FlightLeg): void {
    detail.innerHTML = `
      <h3 class="flight-detail-title">Edit flight ${escapeHtml(id.slice(0, 8))}…</h3>
      <div id="flight-detail-editor" class="flight-detail-solo">${formLegRowHtml()}</div>
      <p class="flight-detail-actions">
        <button type="button" id="flight-detail-save">Save</button>
        <button type="button" class="secondary" id="flight-detail-cancel">Cancel</button>
        <button type="button" class="secondary" id="flight-detail-delete-edit">Delete</button>
      </p>
      <p id="flight-detail-msg" class="cloud-msg" role="status"></p>
    `;
    const editor = detail.querySelector<HTMLElement>("#flight-detail-editor")!;
    const row = editor.querySelector<HTMLElement>("[data-leg-row]")!;
    fillLegRowFromFlightLeg(row, { ...leg, id });
    initLegRowChrome(row, readRatesFromForm(app).fuelLbsPerGallon);
    wireFlightDetailEditorFuel(editor);

    detail.querySelector("#flight-detail-cancel")?.addEventListener("click", () => {
      void (async () => {
        const { data, error } = await getFlightLeg(id);
        const r = readRatesFromForm(app);
        if (error || !data) {
          detail.innerHTML = `<p class="error">${escapeHtml(error ?? "Not found")}</p>`;
          return;
        }
        renderFlightDetailReadOnly(detail, id, data, r);
      })();
    });

    detail.querySelector("#flight-detail-save")?.addEventListener("click", () => {
      void (async () => {
        const msg = detail.querySelector("#flight-detail-msg");
        try {
          if (msg) msg.textContent = "";
          let legs: FlightLeg[] = [];
          try {
            legs = readLegsFromManualForm(
              editor,
              readRatesFromForm(app).fuelLbsPerGallon
            );
          } catch (err) {
            if (msg) msg.textContent = (err as Error).message;
            return;
          }
          if (legs.length !== 1) {
            if (msg) msg.textContent = "Enter one complete leg.";
            return;
          }
          const updated = { ...legs[0]!, id };
          const { error } = await updateFlightLeg(id, updated);
          if (error) {
            if (msg) msg.textContent = error;
            return;
          }
          const { data: fresh, error: fetchErr } = await getFlightLeg(id);
          const r = readRatesFromForm(app);
          if (fetchErr && msg) msg.textContent = fetchErr;
          renderFlightDetailReadOnly(detail, id, fresh ?? updated, r);
          void refreshFlightsTable();
          void pullFlightLegsFromCloudIntoWorksheet({
            focusManualTab: false,
            confirmIfUnsavedManual: false,
          });
        } catch (err) {
          if (msg) msg.textContent = (err as Error)?.message ?? String(err);
        }
      })();
    });

    detail.querySelector("#flight-detail-delete-edit")?.addEventListener("click", async () => {
      if (!window.confirm("Delete this flight row?")) return;
      const { error: delErr } = await deleteFlightLeg(id);
      if (delErr) {
        const m = detail.querySelector("#flight-detail-msg");
        if (m) m.textContent = delErr;
        return;
      }
      detail.classList.add("hidden");
      detail.innerHTML = "";
      void refreshFlightsTable();
      void pullFlightLegsFromCloudIntoWorksheet({
        focusManualTab: false,
        confirmIfUnsavedManual: false,
      });
    });
  }

  async function openFlightLegDetail(id: string): Promise<void> {
    const detail = app.querySelector<HTMLElement>("#flight-detail");
    if (!detail) return;
    if (!id) {
      detail.classList.remove("hidden");
      detail.innerHTML =
        '<p class="error">This row has no flight id. Click <strong>Refresh</strong> above the table and try again.</p>';
      detail.scrollIntoView({ block: "nearest", behavior: "smooth" });
      return;
    }
    detail.classList.remove("hidden");
    detail.innerHTML = "<p>Loading…</p>";
    detail.scrollIntoView({ block: "nearest", behavior: "smooth" });
    const { data, error } = await getFlightLeg(id);
    const rates = readRatesFromForm(app);
    if (error || !data) {
      detail.innerHTML = `<p class="error">${escapeHtml(error ?? "Not found")}</p>`;
      return;
    }
    renderFlightDetailReadOnly(detail, id, data, rates);
    detail.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  async function openFlightLegEditor(id: string): Promise<void> {
    const detail = app.querySelector<HTMLElement>("#flight-detail");
    if (!detail) return;
    if (!id) {
      detail.classList.remove("hidden");
      detail.innerHTML =
        '<p class="error">This row has no flight id. Click <strong>Refresh</strong> above the table and try again.</p>';
      detail.scrollIntoView({ block: "nearest", behavior: "smooth" });
      return;
    }
    detail.classList.remove("hidden");
    detail.innerHTML = "<p>Loading…</p>";
    detail.scrollIntoView({ block: "nearest", behavior: "smooth" });
    const { data, error } = await getFlightLeg(id);
    if (error || !data) {
      detail.innerHTML = `<p class="error">${escapeHtml(error ?? "Not found")}</p>`;
      return;
    }
    renderFlightDetailEdit(detail, id, data);
    detail.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  async function deleteFlightLegFromDataRow(id: string): Promise<void> {
    if (!id) return;
    const msg = app.querySelector<HTMLParagraphElement>("#flights-msg");
    if (!window.confirm("Delete this flight leg from the database? This cannot be undone.")) return;
    const { error } = await deleteFlightLeg(id);
    if (error) {
      if (msg) msg.textContent = error;
      return;
    }
    const detail = app.querySelector<HTMLElement>("#flight-detail");
    if (detail) {
      detail.classList.add("hidden");
      detail.innerHTML = "";
    }
    if (msg) msg.textContent = "";
    void refreshFlightsTable();
    void pullFlightLegsFromCloudIntoWorksheet({
      focusManualTab: false,
      confirmIfUnsavedManual: false,
    });
  }

  async function refreshFlightsTable(): Promise<void> {
    const tbody = app.querySelector("#flights-tbody");
    const msg = app.querySelector("#flights-msg");
    if (!tbody || !showCloud) return;
    if (msg) msg.textContent = "Loading…";
    const { data, error } = await listFlightLegs();
    if (error) {
      if (msg) msg.textContent = error;
      return;
    }
    const n = data?.length ?? 0;
    if (msg) {
      msg.textContent =
        n === 0
          ? "0 rows in flight_legs. With Supabase configured, the worksheet loads from here when you open it (or use Refresh from Supabase on Manual Input)."
          : `${n} row${n === 1 ? "" : "s"}`;
    }
    tbody.innerHTML = "";
    if (n === 0) {
      const tr = document.createElement("tr");
      tr.innerHTML = `<td colspan="5" class="flights-table-empty muted-small">${
        ipadLayout
          ? "No database rows yet. Add legs on <strong>Manual Input</strong> (then <strong>Save manual legs to Supabase</strong>) or enter them elsewhere — the worksheet syncs when the app loads. <strong>Bulk CSV</strong> import is on Mac/PC only."
          : "No database rows yet. Add legs on <strong>Manual Input</strong> (then <strong>Save manual legs to Supabase</strong>), <strong>Bulk Input</strong> (then <strong>Import CSV rows to Supabase</strong>), or enter them in the database elsewhere — the worksheet syncs from this table when the app loads."
      }</td>`;
      tbody.appendChild(tr);
      return;
    }
    for (const leg of data ?? []) {
      const tr = document.createElement("tr");
      const fid = (leg.id ?? "").trim();
      const paxN = dataTableExcisePassengerTotal(leg);
      const tdDate = document.createElement("td");
      tdDate.textContent = leg.flightDate;
      const tdFrom = document.createElement("td");
      tdFrom.textContent = leg.fromIcao;
      const tdTo = document.createElement("td");
      tdTo.textContent = leg.toIcao;
      const tdPax = document.createElement("td");
      tdPax.textContent = String(paxN);
      const tdBtn = document.createElement("td");
      const wrap = document.createElement("div");
      wrap.className = "flights-row-actions";

      const mkBtn = (
        label: string,
        className: string,
        onClick: () => void
      ): HTMLButtonElement => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = className;
        b.textContent = label;
        b.setAttribute("data-flight-id", fid);
        b.addEventListener("click", (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          onClick();
        });
        return b;
      };

      wrap.appendChild(
        mkBtn("View", "secondary flight-open", () => {
          void openFlightLegDetail(fid);
        })
      );
      wrap.appendChild(
        mkBtn("Edit", "secondary flight-edit", () => {
          void openFlightLegEditor(fid);
        })
      );
      wrap.appendChild(
        mkBtn("Delete", "secondary flight-delete", () => {
          void deleteFlightLegFromDataRow(fid);
        })
      );
      tdBtn.appendChild(wrap);
      tr.append(tdDate, tdFrom, tdTo, tdPax, tdBtn);
      tbody.appendChild(tr);
    }
  }

  app.querySelector("#flights-refresh")?.addEventListener("click", () => {
    void refreshFlightsTable();
  });

  app.querySelector("#legs-load-cloud")?.addEventListener("click", async () => {
    const msg = app.querySelector("#legs-cloud-msg");
    if (msg) msg.textContent = "";
    const r = await pullFlightLegsFromCloudIntoWorksheet({ focusManualTab: true });
    if (!r.ok) {
      if (msg) msg.textContent = r.error;
      return;
    }
    if (msg) msg.textContent = `Loaded ${r.count} leg(s) from Supabase.`;
  });

  app.querySelector("#legs-save-cloud")?.addEventListener("click", async () => {
    const msg = app.querySelector("#legs-cloud-msg");
    if (msg) msg.textContent = "";
    let legs: FlightLeg[] = [];
    try {
      legs = readLegsFromManualForm(
        formLegsRoot,
        readRatesFromForm(app).fuelLbsPerGallon
      );
    } catch (e) {
      if (msg) msg.textContent = (e as Error).message;
      return;
    }
    if (legs.length === 0) {
      if (msg) msg.textContent = "No legs to save.";
      return;
    }
    const { error } = await upsertManualLegs(legs);
    if (error) {
      if (msg) msg.textContent = error;
      return;
    }
    const r = await pullFlightLegsFromCloudIntoWorksheet({
      focusManualTab: true,
      confirmIfUnsavedManual: false,
    });
    if (!r.ok) {
      if (msg) msg.textContent = `Saved, but could not reload from cloud: ${r.error}`;
      run();
      return;
    }
    if (msg) msg.textContent = "Saved.";
  });

  app.querySelector("#csv-import-cloud")?.addEventListener("click", async () => {
    const msg = app.querySelector("#csv-cloud-msg");
    const loadMsg = app.querySelector<HTMLParagraphElement>("#csv-load-msg");
    if (msg) msg.textContent = "";
    if (legsCsv.length === 0) {
      if (msg) msg.textContent = "Upload CSV first.";
      return;
    }
    const { error } = await insertFlightLegs(legsCsv);
    if (error) {
      if (msg) msg.textContent = error;
      return;
    }
    const n = legsCsv.length;
    const inserted = `Inserted ${n} row${n === 1 ? "" : "s"} into Supabase.`;
    const r = await pullFlightLegsFromCloudIntoWorksheet({
      focusManualTab: true,
      confirmIfUnsavedManual: false,
    });
    if (!r.ok) {
      if (msg) msg.textContent = `${inserted} Could not refresh worksheet: ${r.error}`;
      if (loadMsg) {
        loadMsg.textContent = inserted;
        loadMsg.classList.add("status-ok");
      }
      return;
    }
    if (msg) msg.textContent = `${inserted} Worksheet updated from cloud.`;
    if (loadMsg) {
      loadMsg.textContent = `${inserted} Worksheet updated from cloud.`;
      loadMsg.classList.add("status-ok");
    }
  });

  for (const id of ["r-dom", "r-intl", "r-pct", "r-akhi"]) {
    app.querySelector(`#${id}`)?.addEventListener("input", () => run());
    app.querySelector(`#${id}`)?.addEventListener("change", () => run());
  }

  app.querySelector("#personal-f720-pct-basis")?.addEventListener("change", () => {
    try {
      const v = app.querySelector<HTMLSelectElement>("#personal-f720-pct-basis")?.value;
      if (v === "off" || v === "fuel" || v === "fuel2x") {
        sessionStorage.setItem(PERSONAL_F720_BASIS_STORAGE, v);
      }
    } catch {
      /* ignore */
    }
    run();
  });

  const loadFile = (f: File) => {
    if (ipadLayout) return;
    const reader = new FileReader();
    reader.onload = () => {
      const loadMsg = app.querySelector<HTMLParagraphElement>("#csv-load-msg");
      const setCsvLoadStatus = (text: string, ok: boolean) => {
        if (!loadMsg) return;
        loadMsg.textContent = text;
        loadMsg.classList.toggle("status-ok", ok);
      };
      try {
        const text = String(reader.result ?? "");
        legsCsv = loadFlightCsv(text);
        hydrateFormWithLegs(
          formLegsRoot,
          [],
          readRatesFromForm(app).fuelLbsPerGallon
        );
        selectLegsTab("sheets");
        const n = legsCsv.length;
        setCsvLoadStatus(`Uploaded ${n} leg${n === 1 ? "" : "s"}.`, true);
      } catch (e) {
        legsCsv = [];
        lastOkLegsRaw = [];
        setCsvLoadStatus("", false);
        render(app, [], readRatesFromForm(app), (e as Error).message, dataSource);
      }
      void airportSuggestRef.refresh();
    };
    reader.readAsText(f, "UTF-8");
  };

  fileInput?.addEventListener("change", () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    loadFile(f);
  });

  addLegBtn.addEventListener("click", () => {
    formLegsRoot.insertAdjacentHTML("beforeend", formLegRowHtml());
    const rows = formLegsRoot.querySelectorAll<HTMLElement>("[data-leg-row]");
    const last = rows[rows.length - 1];
    if (last) initLegRowChrome(last, readRatesFromForm(app).fuelLbsPerGallon);
    run();
  });

  formLegsRoot.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (!t.closest("[data-remove]")) return;
    const card = t.closest("[data-leg-row]");
    card?.remove();
    if (!formLegsRoot.querySelector("[data-leg-row]")) {
      formLegsRoot.innerHTML = formLegRowHtml();
      refreshAllLegRowChrome(formLegsRoot, readRatesFromForm(app).fuelLbsPerGallon);
    }
    run();
  });

  let contractSaveTimer: number | undefined;
  function scheduleHondaContractPersist(): void {
    window.clearTimeout(contractSaveTimer);
    contractSaveTimer = window.setTimeout(() => {
      saveHondaServiceContractToStorage(readHondaServiceContractFromForm(app));
    }, 400);
  }
  for (const id of Object.values(HONDA_CONTRACT_DOM_IDS)) {
    app.querySelector(`#${id}`)?.addEventListener("input", scheduleHondaContractPersist);
    app.querySelector(`#${id}`)?.addEventListener("change", scheduleHondaContractPersist);
  }

  function syncMaintSummaryAnchorForPreset(preset: "month" | "quarter" | "ytd" | "all"): void {
    const yMaint = app.querySelector<HTMLInputElement>("#maint-summary-year");
    const mMaint = app.querySelector<HTMLSelectElement>("#maint-summary-month");
    if (!yMaint || !mMaint) return;
    const now = new Date();
    if (preset === "quarter") {
      const y = now.getFullYear();
      const q = Math.floor(now.getMonth() / 3) + 1;
      const firstMonth = (q - 1) * 3 + 1;
      yMaint.value = String(y);
      mMaint.value = String(firstMonth);
      return;
    }
    yMaint.value = String(now.getFullYear());
    mMaint.value = String(now.getMonth() + 1);
  }

  function applyMaintPeriodPreset(preset: "month" | "quarter" | "ytd" | "all"): void {
    const modeEl = app.querySelector<HTMLSelectElement>("#period-mode");
    const yEl = app.querySelector<HTMLInputElement>("#period-year");
    const qEl = app.querySelector<HTMLSelectElement>("#period-quarter");
    const mEl = app.querySelector<HTMLSelectElement>("#period-month");
    const fb = app.querySelector<HTMLElement>("#maint-preset-feedback");
    if (!modeEl || !yEl || !qEl || !mEl) {
      if (fb) fb.textContent = "Could not find Form 720 date controls.";
      return;
    }
    const now = new Date();
    if (preset === "all") {
      modeEl.value = "all";
    } else if (preset === "ytd") {
      modeEl.value = "ytd";
      yEl.value = String(now.getFullYear());
    } else if (preset === "month") {
      modeEl.value = "month";
      yEl.value = String(now.getFullYear());
      mEl.value = String(now.getMonth() + 1);
    } else {
      modeEl.value = "quarter";
      yEl.value = String(now.getFullYear());
      const q = Math.floor(now.getMonth() / 3) + 1;
      qEl.value = String(q);
    }
    syncMaintSummaryAnchorForPreset(preset);
    applyPeriodAndRun();
    if (fb) {
      fb.textContent = `Form 720 date range set to: ${formatPeriodRangeLabel(readPeriodSelection(app))}. Totals below refreshed.`;
    }
  }

  app.querySelector("#maint-preset-month")?.addEventListener("click", () => {
    applyMaintPeriodPreset("month");
  });
  app.querySelector("#maint-preset-quarter")?.addEventListener("click", () => {
    applyMaintPeriodPreset("quarter");
  });
  app.querySelector("#maint-preset-ytd")?.addEventListener("click", () => {
    applyMaintPeriodPreset("ytd");
  });
  app.querySelector("#maint-preset-all")?.addEventListener("click", () => {
    applyMaintPeriodPreset("all");
  });

  const dz = panelSheets?.querySelector(".dropzone");
  if (dz) {
    dz.addEventListener("dragover", (e) => {
      e.preventDefault();
    });
    dz.addEventListener("drop", (e) => {
      e.preventDefault();
      const f = (e as DragEvent).dataTransfer?.files?.[0];
      if (!f || !/\.csv$/i.test(f.name)) return;
      loadFile(f);
    });
  }

  let contactsImportFile: File | null = null;
  if (showCloud) {
    const contactsMsg = app.querySelector<HTMLParagraphElement>("#contacts-import-msg");
    const contactsFileEl = app.querySelector<HTMLInputElement>("#contacts-import-file");
    app.querySelector("#contacts-import-pick")?.addEventListener("click", () => {
      contactsFileEl?.click();
    });
    contactsFileEl?.addEventListener("change", () => {
      const f = contactsFileEl.files?.[0] ?? null;
      contactsImportFile = f;
      if (contactsMsg) {
        contactsMsg.textContent = f ? `Selected: ${f.name}` : "";
      }
    });
    app.querySelector("#contacts-import-run")?.addEventListener("click", async () => {
      if (!contactsMsg) return;
      contactsMsg.textContent = "";
      if (!contactsImportFile) {
        contactsMsg.textContent = "Choose contacts.json first.";
        return;
      }
      try {
        const text = await contactsImportFile.text();
        const parsed = JSON.parse(text) as unknown;
        if (!Array.isArray(parsed)) {
          contactsMsg.textContent = "JSON must be an array of contact objects.";
          return;
        }
        const rows = parsed as ContactImportJson[];
        const { inserted, error } = await bulkImportContacts(rows);
        if (error) {
          contactsMsg.textContent = error;
          return;
        }
        contactsMsg.textContent = `Imported ${inserted} row(s).`;
        void refreshContactsRoster();
      } catch (e) {
        contactsMsg.textContent = (e as Error).message ?? "Import failed.";
      }
    });
    app.querySelector("#contacts-search")?.addEventListener("input", scheduleContactsSearch);
    app.querySelector("#contacts-roster-refresh")?.addEventListener("click", () => {
      void refreshContactsRoster();
    });
  }

  const getLegsSnapshotForAirports = (): FlightLeg[] => {
    const out: FlightLeg[] = [...legsCsv];
    if (showCloud) out.push(...worksheetCloudLegs);
    try {
      out.push(
        ...readLegsFromManualForm(
          formLegsRoot,
          readRatesFromForm(app).fuelLbsPerGallon
        )
      );
    } catch {
      /* incomplete manual rows */
    }
    return out;
  };
  const airportAc = initAirportsAutocomplete(app, {
    hasSupabase: showCloud,
    getLegsSnapshot: getLegsSnapshotForAirports,
  });
  airportSuggestRef.refresh = airportAc.refreshAirports;

  initPassengersAutocomplete(app, { contactsSearchEnabled: showCloud });

  app.addEventListener("change", (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement) || t.name !== "summary-quarter-pick") return;
    if (!summaryScopedQuartersCache) return;
    const report = app.querySelector("#summary-period-report");
    if (!report) return;
    const validKeys = [...summaryScopedQuartersCache.keys()];
    let checked = [
      ...report.querySelectorAll<HTMLInputElement>(
        'input[name="summary-quarter-pick"]:checked'
      ),
    ].map((i) => i.value).filter((k) => validKeys.includes(k));
    if (checked.length === 0) {
      const first = report.querySelector<HTMLInputElement>(
        'input[name="summary-quarter-pick"]'
      );
      if (first) {
        first.checked = true;
        checked = [first.value];
      }
    }
    writeSummaryQuarterSelectionKeys(checked);
    const body = app.querySelector("#summary-quarter-detail-body");
    const ctx = summaryQuarterDetailCtx;
    if (body && ctx) {
      body.innerHTML = quarterDetailCombinedHtml(
        summaryScopedQuartersCache,
        checked,
        ctx.scopedLegs,
        ctx.rates,
        ctx.isPersonalLeg,
        summaryPersonalPct720Cache
      );
    }
  });

  app.querySelector("#form720-download-pdf")?.addEventListener("click", async () => {
    const msgEl = app.querySelector<HTMLParagraphElement>("#form720-pdf-msg");
    const setMsg = (s: string) => {
      if (msgEl) msgEl.textContent = s;
    };
    setMsg("");
    try {
      const rates = readRatesFromForm(app);
      const rollup = readRollupCtx(app);
      const sel = readPeriodSelection(app);
      const legsScoped = filterLegsByPeriod(currentLegs(), sel).filter(rollup.isPersonalLeg);
      const qMap = aggregateByQuarter(legsScoped, rates, {
        isPersonalLeg: rollup.isPersonalLeg,
      });
      const validKeys = [...qMap.keys()];
      if (validKeys.length === 0) {
        setMsg(
          "No personal-classified legs in the Form 720 date range — there is no excise to place on the PDF."
        );
        return;
      }
      const checked = readSelectedQuarterKeysForF720Pdf(app, validKeys);
      const parts = checked
        .map((k) => qMap.get(k))
        .filter((x): x is QuarterlyTotals => x !== undefined);
      if (parts.length === 0) {
        setMsg("Could not resolve selected quarter(s) for the PDF.");
        return;
      }
      const combined = combineQuarterlyTotals(parts);
      if (!combined) {
        setMsg("Could not combine selected quarters.");
        return;
      }
      const pctBasis = readPersonalF720PctBasisFromForm(app);
      const personalByQ = personalTransportPercentageTaxByQuarter(legsScoped, rates, {
        basis: pctBasis,
        isPersonalLeg: rollup.isPersonalLeg,
      });
      let pPersonal = 0;
      if (pctBasis !== "off") {
        for (const k of checked) pPersonal += personalByQ.get(k) ?? 0;
        pPersonal = money(pPersonal);
      }
      const irs26Tax = money(
        combined.irs26PercentageTax + combined.irs26DomesticSegmentTax + pPersonal
      );
      const irs27Tax = money(
        combined.irs27InternationalFacilityTax + combined.irs26AkHiDepartureTax
      );

      const base = import.meta.env.BASE_URL.replace(/\/?$/, "/");
      const res = await fetch(`${base}irs/f720.pdf`);
      if (!res.ok) {
        setMsg(`Could not load PDF template (${res.status}). Is public/irs/f720.pdf present?`);
        return;
      }
      const template = new Uint8Array(await res.arrayBuffer());
      const out = await buildFilledForm720Pdf(
        template,
        {
          ...form720PdfFilerBase,
          quarter_ending: latestQuarterEndingLabel(parts),
        },
        {
          irs26Tax,
          irs27Tax,
        }
      );
      const blob = new Blob([out.buffer.slice(out.byteOffset, out.byteOffset + out.byteLength)], {
        type: "application/pdf",
      });
      const label = checked
        .map((k) => {
          const q = qMap.get(k);
          return q ? `${q.year}-Q${q.quarter}` : k;
        })
        .sort()
        .join("_");
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `Form720-draft-${label}.pdf`;
      a.rel = "noopener";
      document.body.append(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setMsg(
        "Download started. PDF is flattened (values drawn on the page) so quarter ending and EIN show in the right boxes in Preview/Chrome/Adobe. Filer block plus Part I IRS No. 26 (persons) and No. 27 (international + AK/HI) are filled; No. 28 (property) and Claims / Schedule C are not — verify with your preparer."
      );
    } catch (e) {
      setMsg((e as Error).message ?? "Could not build PDF.");
    }
  });

  void (async () => {
    if (
      showCloud &&
      import.meta.env.VITE_SKIP_SUPABASE_AUTOLOAD !== "1"
    ) {
      try {
        const r = await pullFlightLegsFromCloudIntoWorksheet({
          focusManualTab: true,
          confirmIfUnsavedManual: false,
        });
        if (r.ok) return;
      } catch {
        /* ignore */
      }
    }
    run();
  })();
}

function main(): void {
  const app = document.querySelector<HTMLDivElement>("#app")!;
  if (isAuthed()) {
    mountWorksheet(app);
  } else {
    renderLogin(app);
  }
}

main();
